/**
 * RBAC types and utilities shared between server and client.
 *
 * ## Architecture (read before changing anything in this file)
 *
 * The CANONICAL role-data-access lookup is `getRoleDataAccessFromD1()` in
 * `src/server/lib/rbac.ts`. It reads `allowed_tools`, `allowed_table_prefixes`,
 * and `data_restriction_note` from the D1 `roles` table on every request.
 *
 * This file only holds:
 *  - Type definitions (Role, UserRole, RoleDataAccess, etc) shared across surfaces
 *  - The AI tool registry (`ALL_AI_TOOLS`) used by the Admin role-edit UI
 *  - The Ostendo resource → module map (`OSTENDO_RESOURCE_MODULE`)
 *  - `ROLE_FALLBACK_DEFAULTS` — fallback defaults used by getRoleDataAccessFromD1
 *    when D1 returns no row OR when the D1 read fails (outage resilience for the
 *    6 built-in role IDs). NOT the primary access source.
 *  - Pure helper functions (isToolAllowed, isOstendoResourceAllowed,
 *    extractTableNames, isTableNameAllowed, isSqlRelationDenied, isPathAllowed)
 * The SQL execution gate (checkTableAccess, checkToolCallAccess) lives in
 * server/lib/sql-gate.ts so the PostgreSQL parser never reaches the browser.
 *
 * **If you're updating which tools a built-in role has access to** in
 * production: update the D1 `roles` row via Admin UI or cf_d1 — NOT this file.
 * Editing `ROLE_FALLBACK_DEFAULTS` only affects fallback behaviour during D1
 * outages, not normal operation.
 *
 * **If you're adding a new built-in role**: add it to `ROLE_FALLBACK_DEFAULTS`
 * AND seed it into D1 via a migration. Both are required.
 *
 * GH #49 (closed 2026-05-08, briefly re-opened 2026-05-24 in error): the
 * legacy `getRoleDataAccess()` sync function lived here, was replaced by the
 * D1-backed async path, then deleted. Marcus Jr's 2026-05-24 re-open looked
 * at the legacy comment in this file and missed the new path in
 * server/lib/rbac.ts; that audit shape is why this top-of-file note exists.
 */

import { ALL_PAGE_PATHS, PATH_ALIASES } from "./sidebar-pages";
import { QA_PRODUCTS } from "./products";
import { OSTENDO_RESOURCE_MODULE_FROM_CATALOG } from "./ostendo-catalog";
import { CROS_PURCHASING_RESOURCES, CROS_PURCHASING_UNSLICED_ROLE_IDS } from "./commercial-roles";
import { syntheticAgentTools } from "./synthetic-agents";
import { EXACT_TABLE_MARK, codeDefinedGrants, honoursExactGrants } from "./table-grants";

export interface Role {
  id: string;
  name: string;
  description: string | null;
  allowed_pages: string[];
  allowed_apis: string[];
  is_default: boolean;
}

export interface UserRole {
  user_id: string;
  user_email: string;
  role_id: string;
  role_slug: string;
  role_name: string;
  allowed_pages: string[];
  allowed_apis: string[];
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  image: string | null;
  createdAt: string;
  /** #737: the PRIMARY role (precedence ladder) — display/back-compat. */
  role_id: string | null;
  role_name: string | null;
  /** #737: ALL assigned roles, precedence-sorted. Permissions = union of these. */
  roles: { role_id: string; role_slug: string; role_name: string }[];
  assigned_by: string | null;
  last_login: string | null;
  session_count: number;
}

/** #423: one per-user feature override row (as served by /api/rbac/users/:email/overrides). */
export interface FeatureOverrideRow {
  feature_key: string;
  effect: "grant" | "revoke";
  assigned_by: string | null;
  created_at: string;
  /**
   * #3406: the Ostendo capabilities an `/api/...` key carries, derived
   * server-side with the seed's rule. A grant row carries them to the user;
   * a revoke row withholds them. Absent (not empty) on a key that is not an
   * API prefix. Display only: the authorizer derives its own.
   */
  carries?: { capability: string; label: string }[];
}

export interface AdminRole {
  id: string;
  role_slug: string;
  name: string;
  description: string | null;
  allowed_pages: string[];
  /** #2866: true when the stored allowed_pages value did not parse -- the list is
   *  then [] (fail closed) and the UI must not render that as "0 pages" the way a
   *  deliberately empty grant would look. */
  allowed_pages_unreadable?: boolean;
  allowed_apis: string[];
  allowed_tools: string[];
  allowed_table_prefixes: string[];
  allowed_write_actions: (WriteAction | "*")[];
  data_restriction_note: string;
  /** #1125 Slice B: 1 = members see only rd_family_grants families. */
  rd_family_scoped: number;
  is_default: number;
  user_count: number;
}

/** Canonical AI tool registry — used by Admin role-edit UI. Keep aligned with src/server/lib/sql-tool.ts. */
export const ALL_AI_TOOLS: { name: string; group: string; description: string }[] = [
  { name: "list_tables", group: "Database", description: "Browse Postgres table catalogue" },
  { name: "get_table_schema", group: "Database", description: "Read columns and types for a table" },
  { name: "run_sql", group: "Database", description: "Execute read-only SQL against analytics DB" },
  { name: "read_data", group: "Database", description: "Stream large result sets to R2 data lake" },

  { name: "search_kb", group: "Domain", description: "FTS5 search over the knowledge base" },
  { name: "get_product_info", group: "Domain", description: "Look up product catalogue + TDS/SDS" },
  { name: "get_product_tds", group: "Domain", description: "Official-vs-internal TDS retrieval with QC field mapping (#1150 source separation)" },
  { name: "compare_competitor_product", group: "Domain", description: "Gap-first competitor-product equivalence from product technical evidence" },
  { name: "get_spec_limits", group: "Domain", description: "Fetch QA spec limits per product" },
  { name: "get_production_sequence", group: "Domain", description: "Return ordered batch sequence" },
  { name: "get_recent_failures", group: "Domain", description: "Scan QA tables for out-of-spec results" },
  { name: "count_tests", group: "Domain", description: "Count QC tests (not samples) per product per family for a date window — implied by run_sql + qa_production_ access (#2114)" },
  { name: "get_material_costs", group: "Domain", description: "Material cost data (sensitive — admin/management only)" },
  { name: "calculate_carbon", group: "Domain", description: "Per-product CO2e from formulation BOM" },
  { name: "resolve_material_identity", group: "Domain", description: "Resolve a raw material phrase to canonical Ostendo identity + Akira-approved fact (when available)" },
  { name: "resolve_product_identity", group: "Domain", description: "Resolve a product phrase to canonical live product identity, aliases, and variant siblings" },
  { name: "geocode_address", group: "Domain", description: "Geocode / validate / reverse-lookup Australian addresses via G-NAF (geomcp.au) — lat/lng, normalised address, mapping" },
  { name: "read_webpage", group: "Research", description: "Read the text of a public web page by URL (competitor/supplier sites, news, standards) — SSRF-guarded, read-only" },
  { name: "business_lookup", group: "Research", description: "Look up an Australian business in the ABR by name or ABN — entity, status, GST, ACN, trading names (customer/competitor enrichment)" },
  { name: "web_search", group: "Research", description: "Search the web for competitor/market/customer external intelligence: ranked sources with snippets; pairs with read_webpage" },
  { name: "competitor_profile", group: "Research", description: "Build a structured profile of a competitor by name — ABR registry (entity/status/ABN) + web findings (products, news, pricing signals) + sources, in one call (#650)" },

  { name: "calculate", group: "Analytics", description: "Arithmetic + numeric operations" },
  { name: "calculate_process_stats", group: "Analytics", description: "Cp/Cpk + mean/stddev computations" },
  { name: "aggregate_data", group: "Analytics", description: "Sum/avg/count over R2 result sets" },
  { name: "pivot_data", group: "Analytics", description: "Pivot rows by group" },
  { name: "compare_groups", group: "Analytics", description: "Two-sample comparison" },
  { name: "trend_analysis", group: "Analytics", description: "Time-series trend" },
  { name: "distribution", group: "Analytics", description: "Histogram + percentiles" },
  { name: "batch_analysis", group: "Analytics", description: "Aggregate over multiple batches" },
  { name: "get_cached_summary", group: "Analytics", description: "Read pre-computed daily KPI cache" },

  { name: "create_chart", group: "Visualisation", description: "Render an inline Recharts chart" },
  { name: "create_artifact", group: "Visualisation", description: "Pin chart/table as named artifact (sensitive)" },
  { name: "export_data", group: "Visualisation", description: "Generate CSV/JSON download" },

  // #352 / #290: per-DB Ostendo access is set by ticking the tool, not a sentence.
  // The legacy single `query_ostendo` is retained as a back-compat alias in the
  // executor only — it is intentionally NOT offered here so new role configs use
  // the DB-scoped tools (all built-in roles are already migrated off the alias).
  { name: "query_ostendo_coop", group: "ERP", description: "Query COOP — Coopers manufacturing DB (materials, items, BOMs, inventory, purchasing, labour, cost history)" },
  { name: "query_ostendo_cros", group: "ERP", description: "Query CROS — Crosbie sales DB (customers, sales orders, invoices, budget; CROS purchasing incl. supplier invoices for the allow-set roles)" },
  { name: "trace_order", group: "ERP", description: "Reverse-trace a customer order → batch → raw lots → PO (cross-DB; admin/management)" },
  // #1748: these two were dispatched in chat.ts but missing from this registry
  // — the admin role editor could not grant what it could not list.
  { name: "trace_recall", group: "ERP", description: "Forward-trace a batch/lot → every order and customer it shipped in (recall fan-out)" },
  { name: "get_presentation_feedback", group: "Tech Services", description: "Read presentation attendee and submitted feedback results (case_manage + /api/ts-records required)" },
  // #3538: page-gated like the entry above. Holding the page's API grant is the
  // whole rule (sales_rep excepted, server/lib/analyst-cases.ts); ticking the
  // tool on a role neither adds nor removes reach.
  { name: "read_cases", group: "Domain", description: "Read Cases (complaints, account, site, product, status) as the Cases page shows them (/api/cases required)" },
  { name: "read_projects", group: "Domain", description: "Read the investigation projects the user is a member of, as the Projects page lists them (/api/projects required)" },
  // #4054: page-gated the same way. Reach is the page's API action and path
  // grant plus the page itself (server/lib/tds-figures-tool.ts); ticking the
  // tool on a role neither adds nor removes reach.
  { name: "get_tds_figures", group: "Domain", description: "Read a product's internal TDS figures (publication head and open drafts), as the TDS Editor holds them (a TDS capability, /api/tds and /tds required)" },
  { name: "get_qc_tds_claims", group: "Domain", description: "Read a product's QC vs TDS workbook rows and TDS claims beside the crosbe.com revision (view_qc_vs_tds, /api/qc-vs-tds and the page required)" },
  // #4408: page-gated the same way. Reach is /api/product-inventory and the
  // page (server/lib/product-inventory-tool.ts); ticking the tool on a role
  // neither adds nor removes reach.
  { name: "read_product_inventory", group: "Domain", description: "Read the Product Inventory board as the page shows it: stock by site and product group, held by QC, In-Transfer, On Order and today's runs (/api/product-inventory and /product-inventory required)" },
  // #4920: page-gated the same way. Reach is the Weigh-Out page, /api/formulations
  // and the capabilities of the page's reads (server/lib/recipe-lines-tool.ts);
  // ticking the tool on a role neither adds nor removes reach.
  // #5188: page-gated the same way. Reach is the Aged Debtors or Debtor
  // Reminders capability and its route's API path
  // (server/lib/credit-analyst-tools.ts); ticking either neither adds nor
  // removes reach.
  { name: "read_aged_debtors", group: "Domain", description: "Read the Aged Debtors figures per customer as the page shows them: buckets, overdue, credit limit and stop, term (commercial.aged_debtors or commercial.aged_debtors_reminders, and its API, required)" },
  { name: "read_debtor_reminders", group: "Domain", description: "Read the Debtor Reminders queue, each draft's status and the settings, with no addresses (commercial.aged_debtors_reminders and its API required)" },
  { name: "read_recipe_lines", group: "Domain", description: "Read a product's current recipe lines as Weigh-Out serves them: material, Ostendo code and kg, no costs (/api/formulations, /weigh-out and the Weigh-Out reads' capabilities required)" },
  { name: "trace_material", group: "ERP", description: "Trace a raw-material lot → the batches and products that consumed it" },
  // #2695: the capability-gated product-sales surface. Granted by holding
  // commercial.product_sales (the manifest's `tools` map adds it in
  // getUserDataAccess), which is how a Sales Rep reaches product units without
  // query_ostendo_cros, the arbitrary-SQL tool #1392 denies them.
  { name: "product_sales", group: "ERP", description: "Product sales units by item and month (sales_history, server-filtered: units, item codes, dates and tonnes; no prices, costs or customer)" },
  { name: "risk_accounts", group: "ERP", description: "Customer churn radar — active customers drifting toward inactive (CROS invoice cadence, ranked by revenue at risk)" },
  { name: "save_memory", group: "Memory", description: "Save a durable per-user fact (preference/focus/context) for future conversations (#601)" },
  { name: "list_memories", group: "Memory", description: "List the user's stored memories (#601)" },
  { name: "forget_memory", group: "Memory", description: "Forget a stored memory by id or content match (#601)" },
];

// ─── Write / action permission layer (#349) ──────────────────
// crosbe-ai's RBAC was read/page/AI-tool oriented (allowed_pages/apis/tools/
// table_prefixes) — it had NO write-permission model. The QC rollout (#338/#339)
// is the first real WRITE surface, so this adds one: a role gates the *actions*
// it may perform, not just the pages it may see. Granting the /qa-entry page is
// NOT the same as "can enter a sample" — that's `enter_sample`, gated here.
//
// Grounded in crosbe-app's live QC permission tiers (Viewer→Analyst→Manager),
// with Akira's deliberate divergence: editing spec Configs moves UP to manager
// (analysts enter/correct data only). Generalises to NATA/Calibration/Production
// Monitor/R&D when they roll over — build the tier shape once, apply per module.

/** A gated write/action capability. "*" (in a role's allow-list) = all actions. */
export type WriteAction =
  | "enter_sample" // create a QC sample/result row
  | "edit_sample" // correct an existing sample/result
  | "edit_template" // edit a test template (#348 template engine)
  | "edit_spec_config" // edit spec limits / control bands (manager-only per Akira)
  | "set_approval" // approve / hold / reject a result (distinct from edit)
  | "release_batch" // #2283 APPRMON: release a batch for despatch on the Approval Monitor, and withdraw a release with a reason. qc_manager + qa_tech (Akira, 10 Oct 2026: "available to both, as long as it is fully auditable"; D1 row in migrate-2283b-release-batch-grants.sql); its own action, not set_approval, so the grant is visible and revocable on its own
  | "delete_sample" // soft-delete a sample (manager-only)
  | "cancel_sample" // FGCANCEL #4857: cancel a Finished Goods sample entered by mistake (status cancelled, never a DELETE). qc_manager only (Akira, 2 Nov 2026); D1 row in migrate-4857-sample-cancel-grants.sql. Grantable per-user via overrides (write:cancel_sample), like bom_approve, for a named QC technician
  | "restore_sample" // FGCANCEL #4857: restore a cancelled Finished Goods sample to active, and see cancelled samples on Records. qc_manager only
  | "kb_write" // create / edit / delete knowledge-base articles (#513; management + admin)
  | "mixdesign_write" // create / edit R&D mix designs (#502; rd + admin)
  | "edit_rd_ingredient" // create / edit R&D ingredients (research_rawmaterial) (#789; rd + technical_manager + admin)
  | "formulation_write" // create / edit production formulations (#815; promote separately requires rd/technical_manager)
  | "create_upi" // create a UPI / batch register row (#691; QC + admin)
  | "edit_upi" // edit an existing UPI row (#691; QC + admin)
  | "assign_upi" // attach / change the UPI on an existing test (#691)
  | "edit_test_types" // toggle a product's active test-type flags (#342, manager-only per Akira)
  | "set_batch_alarm" // set/clear a production batch-count alarm (#343, floor-level action; qc_analyst + qc_manager + qa_tech, #3792)
  | "edit_equipment" // create/edit lab equipment + calibration records (#392; qc_manager + technical_manager + admin). NATA lab; DELETE calibration is a hard delete — manager-gated on purpose.
  | "rdti_log" // bundle: task+walk, own time, own lock, own crumbs
  | "rdti_manage" // bundle: projects, review others, Clockify admin
  | "rdti_task_write" // create/edit RDTI tasks (projects remain manager-owned)
  | "rdti_gate_walk" // submit the eligibility walk used by task create/edit
  | "rdti_entry_write" // log/edit/delete OWN RDTI time entries
  | "rdti_lock_own" // lock OWN confirmed RDTI entries
  | "rdti_crumb_write" // add/edit OWN RDTI evidence crumbs
  | "rdti_report_others" // read-only reports across the team
  | "rdti_review_others" // review/unlock/approve OTHER users' entries
  | "rdti_project_manage" // create/edit RDTI project structure
  | "rdti_clockify_admin" // run/read the RDTI Clockify admin module
  | "write_commercial_note" // add/pin an Ops & CS note on the Commercial Hub (#585; ANY hub-access role — Hugo's call)
  | "write_pipeline_deal" // add/edit/tick/delete a Sales Pipeline entry (#2159; sales_manager + sales_rep + admin, Akira 8 Sep 2026; ceo_ops added 1 Oct 2026, ANDREWPIPE. Reset is tighter: reset_pipeline_board)
  | "reset_pipeline_board" // #3327: clear the whole Sales Pipeline board (sales_manager + admin; was a route-level role-id gate)
  | "write_competitor_price" // COMPPRICE #3571: log a competitor price sighting, and delete your own (sales_manager + sales_rep + ceo_ops + admin)
  | "delete_any_competitor_price" // COMPPRICE #3571: delete anyone's competitor price sighting, backfilled rows included (sales_manager + admin)
  | "view_qc_vs_tds" // #2545: read QC vs TDS (tech_services + qc_manager (#3740) + admin; was a route-level role gate). A read, gated as an action so admin's "*" and a per-user revoke both reach it
  | "edit_tds_claim_standards" // #3740 TDSSTD: record the standard and test method behind a TDS claim on QC vs TDS > Claims vs tests (tech_services + qc_manager + admin). Needs view_qc_vs_tds too
  | "troubleshooting_ask" // create a Troubleshooting question — guided or chat (#587; rep/commercial + technical_services + management + admin)
  | "troubleshooting_review" // add/edit/delete a reviewer note + mark reviewed (#587; technical_services + admin)
  | "project_manage" // create/mutate Projects-module content — beats, concerns, waiting-on, evidence (#740/#741/#743; management + rd + admin)
  | "tds_author" // System 1 (#1184): create/edit TDS drafts, field values, checkpoints
  | "tds_review" // System 1 (#1184): record an approval verdict on a TDS draft
  | "tds_publish" // System 1 (#1184): issue an INTERNAL published revision (not website-official — that registry stays admin-only)
  | "tds_template_manage" // System 1 (#1184): create/append TDS layout template revisions (affects every product)
  | "tds_bootstrap_import" // System 1 (#1187): seed a document's ENTIRE historical lineage from a legacy extraction, one time, before any native authoring begins
  | "schedule_edit" // ScheduleIt mirror: create/edit/delete bookings in cai (testing phase; the scheduled sync overwrites scheduleit-sourced events)
  | "rm_planner_edit" // RM reordering planner: edit supplier lead-time/safety-stock config + production-run→SKU mapping
  | "bom_approve" // #1169: approve BOM review changes — ceo_ops/admin by role, grantable per-user via overrides so e.g. Tolu/Andy G can approve without the whole ceo_ops role
  | "bom_sign_finance" // #3222 BOMDUAL: sign the Finance seat of a BOM version. Per-user grant only (no role carries it), so Tolu's finance role does not admit her
  | "bom_sign_ops" // #3222 BOMDUAL: sign the Ops seat of a BOM version. Per-user grant only, so Albert's ceo_ops role does not admit him
  | "rm_qc_write" // Raw Material QC: record panel samples + sieve runs (qc_analyst + qc_manager + admin)
  | "rm_qc_admin" // Raw Material QC: manage the material/test vocabulary and panels (qc_manager + admin)
  | "material_mapping_write" // #2731: re-point an alias, rename a material, dismiss an unresolved alias. admin, technical_manager, qc_manager, rd. finance + ceo_ops keep read-only (admin.material_mapping) via #2565
  | "rd_material_costs" // #1125 A: SEE material costs (price_per_kg) in R&D reads — read-projection capability, rd + technical_manager + admin
  | "promote_spec_version" // #1697: change WHICH spec config version is in force (distinct from edit_spec_config = edit the numbers; Marcus's lean, Hugo notified pre-ship)
  | "mint_qa_product" // #1579: create a new QC product card by cloning an existing one (app-driven DDL — typed confirm + audit; qc_manager + admin)
  | "case_open" // #2056 Cases: open a case; add an account, site, batch or action (commercial + technical roles)
  | "visit_log" // #2056 Cases: log a site visit with photos/video (same roles as case_open)
  | "case_manage" // #2056 Cases: change status / inquiry class / responsible, resolve, export (technical_services + management + admin; per-user overrides for named Tech Services staff)
  | "review_ideas" // #926: approve or reject an idea on Updates; approving files a GitHub issue. Admin-only (the routes also check admin); the action exists so a per-user write revoke reaches an admin (#3378). No role carries it
  | "morning_run_rehearse" // #4618 MORNINGFLOW: start a rehearsal of the whole 07:00 morning run on staging (POST /api/admin/morning-run/rehearse; refused on production). Admin-only (the route also checks admin); the action exists so a per-user write revoke reaches an admin (#3378). No role carries it
  | "manage_qc_stations" // #4447 GUSSTATION: register, revoke and unlock QC stations, set the PIN length. Admin-only (requireAdminMiddleware on /api/station-admin); the action exists so a per-user write revoke reaches an admin (#3378). No role carries it
  | "manage_help_inbox" // #4468 HELPINBOX: answer help@crosbe.ai requests and edit who may email it. Admin-only (requireAdminMiddleware on /api/help-inbox); the action exists so a per-user write revoke reaches an admin (#3378). No role carries it
  | "manage_team_facts"; // #4497 AIHGLOSSARY: add, edit and retire team facts (Admin > Team facts, and the save_team_fact chat tool). Admin-only (requireAdminMiddleware on /api/team-facts, isAdmin on the tool); the action exists so a per-user write revoke reaches an admin (#3378). No role carries it

/**
 * #3687 REFUSALNOTE: the actions that gate a READ, not a write. They ride the
 * write-action machinery so admin's "*" and a per-user revoke reach them, but
 * a refusal of one is not a refused write, so requireWriteAction answers
 * "Not permitted" for these and keeps "Write not permitted" for the rest.
 * Wording only: membership here changes no grant and no gate.
 */
export const VIEW_ACTIONS: ReadonlySet<WriteAction> = new Set<WriteAction>([
  "view_qc_vs_tds",
  "rd_material_costs",
]);

/** True when the action gates a read (see VIEW_ACTIONS). */
export function isViewAction(action: WriteAction): boolean {
  return VIEW_ACTIONS.has(action);
}

/**
 * #2545 QCTDS: the write action /api/qc-vs-tds gates on (both mount forms in
 * src/server/index.ts). Akira opened the page to Technical Services on 24 Sep
 * 2026; technical_manager was left out on purpose. #3740 TDSSTD adds
 * qc_manager. tech_services and qc_manager hold it (ROLE_FALLBACK_DEFAULTS and
 * the held grant files), admin through "*". Shared with
 * tests/qc-vs-tds-access-2545.test.ts so the gate and the test cannot drift.
 * Lives here, not in index.ts, because a Worker entry may export only
 * handlers and classes (#4391).
 */
export const QC_VS_TDS_ACTION = "view_qc_vs_tds" satisfies WriteAction;

/**
 * #2550: the roles the /api/ostendo/flush-waste routes admit (admin, ceo_ops,
 * finance), held as the finance.flush_waste capability grant the production
 * seed gives them. tests/flush-waste-gating.test.ts pins this list and seeds
 * from it. Moved out of src/server/index.ts by #4391 (Worker entry exports).
 */
export const FLUSH_WASTE_ROLES: readonly string[] = ["admin", "ceo_ops", "finance"];

/**
 * #1184 — the TDS capability set. Holding ANY of these is what grants READ
 * access to /api/tds (the viewing floor in index.ts); each mutation route then
 * gates on its specific action. Kept as one list so the floor and the
 * per-action gates can never drift apart.
 */
export const TDS_CAPABILITIES: readonly WriteAction[] = [
  "tds_author",
  "tds_review",
  "tds_publish",
  "tds_template_manage",
  "tds_bootstrap_import",
];

/** Canonical write-action registry — for the Admin role-edit UI + docs. Mirror of ALL_AI_TOOLS. */
export const ALL_WRITE_ACTIONS: { name: WriteAction; group: string; description: string }[] = [
  { name: "enter_sample", group: "QA Entry", description: "Create a QC sample / test result" },
  { name: "edit_sample", group: "QA Entry", description: "Correct an existing sample / result" },
  { name: "set_approval", group: "QA Entry", description: "Approve / hold / reject a result (lead governance)" },
  { name: "release_batch", group: "QA Entry", description: "Release a batch for despatch on the Approval Monitor, and withdraw a release with a reason (#2283)" },
  { name: "delete_sample", group: "QA Entry", description: "Soft-delete a sample (manager only)" },
  { name: "cancel_sample", group: "QA Entry", description: "Cancel a Finished Goods sample entered by mistake, with a reason; it leaves Records and charts and is never deleted (#4857)" },
  { name: "restore_sample", group: "QA Entry", description: "See cancelled Finished Goods samples on Records and restore one, with a reason (#4857, manager only)" },
  { name: "create_upi", group: "AO tail", description: "Create an AO tail batch record (#691)" },
  { name: "edit_upi", group: "AO tail", description: "Edit an AO tail batch record (#691)" },
  { name: "assign_upi", group: "AO tail", description: "Attach / change the AO tail on an existing test (#691)" },
  { name: "edit_template", group: "Config", description: "Edit a test template (#348)" },
  { name: "edit_spec_config", group: "Config", description: "Edit spec limits / control bands (manager only)" },
  { name: "edit_test_types", group: "Config", description: "Toggle a product's active test-type flags (#342, manager only)" },
  { name: "set_batch_alarm", group: "Production", description: "Set / clear a production batch-count alarm (#343)" },
  { name: "edit_equipment", group: "Lab", description: "Create / edit lab equipment + calibration records (#392; manager-gated — NATA lab)" },
  { name: "kb_write", group: "Knowledge Base", description: "Create / edit / delete KB articles (#513)" },
  { name: "mixdesign_write", group: "R&D", description: "Create / edit R&D mix designs (#502; rd + admin)" },
  { name: "edit_rd_ingredient", group: "R&D", description: "Create / edit R&D ingredients (research raw materials) (#789; rd + technical_manager + admin)" },
  { name: "formulation_write", group: "R&D", description: "Create / edit production formulations (#815; promote separately requires rd/technical_manager)" },
  { name: "rdti_log", group: "RDTI bundles", description: "Self-service bundle: tasks + walk + own time + own lock + own crumbs" },
  { name: "rdti_manage", group: "RDTI bundles", description: "Rare admin bundle: projects + review others + Clockify admin" },
  { name: "rdti_task_write", group: "RDTI", description: "Create and edit RDTI tasks (not projects)" },
  { name: "rdti_gate_walk", group: "RDTI", description: "Submit the server-derived RDTI eligibility walk" },
  { name: "rdti_entry_write", group: "RDTI", description: "Log, edit and delete own RDTI time entries" },
  { name: "rdti_lock_own", group: "RDTI", description: "Lock own confirmed RDTI time entries" },
  { name: "rdti_crumb_write", group: "RDTI", description: "Add and edit own RDTI evidence crumbs" },
  { name: "rdti_report_others", group: "RDTI", description: "View and export team hours without review or editing access" },
  { name: "rdti_review_others", group: "RDTI", description: "Review, unlock and approve other users' entries" },
  { name: "rdti_project_manage", group: "RDTI", description: "Create and edit the manager-owned RDTI project structure" },
  { name: "rdti_clockify_admin", group: "RDTI", description: "Use RDTI Clockify import, sync, backfill and identity admin" },
  { name: "write_commercial_note", group: "Commercial", description: "Add / pin an Ops & CS note on the Commercial Hub (#585)" },
  { name: "write_pipeline_deal", group: "Commercial", description: "Add / edit / confirm / remove a Sales Pipeline deal (#2159). Clearing the whole board is reset_pipeline_board." },
  { name: "reset_pipeline_board", group: "Commercial", description: "Clear the whole Sales Pipeline board, every rep's deals (#3327; Sales Manager). Needs write_pipeline_deal too." },
  { name: "write_competitor_price", group: "Commercial", description: "Log a competitor price on Competitor Prices, and delete your own entries (#3571)." },
  { name: "delete_any_competitor_price", group: "Commercial", description: "Delete anyone's Competitor Prices entry, including loaded-in history (#3571; Sales Manager). Needs write_competitor_price too." },
  { name: "view_qc_vs_tds", group: "Commercial", description: "Open QC vs TDS, the TDS claim against live QC results (#2545; Technical Services, QC Manager). Read-only; it carries formulation sand percentages." },
  { name: "edit_tds_claim_standards", group: "Commercial", description: "Edit the standard and test method behind each TDS claim on QC vs TDS, Claims vs tests (#3740; Technical Services, QC Manager). Every change is audited. Needs view_qc_vs_tds too." },
  { name: "troubleshooting_ask", group: "Commercial", description: "Ask a Troubleshooting question — guided or chat (#587)" },
  { name: "troubleshooting_review", group: "Commercial", description: "Review a Troubleshooting answer — add/edit/delete note, mark reviewed (#587; Technical Services)" },
  { name: "case_open", group: "Commercial", description: "Open a case; add an account, site, batch or action (Cases)" },
  { name: "visit_log", group: "Commercial", description: "Log a site visit with photos/video (Cases)" },
  { name: "case_manage", group: "Commercial", description: "Change case status, inquiry class, responsible; resolve; export (Cases; Technical Services)" },
  { name: "project_manage", group: "Projects", description: "Create / edit Projects content — beats, concerns, waiting-on, evidence (#740/#741/#743)" },
  { name: "tds_author", group: "TDS", description: "Write TDS drafts — editor content, field values, checkpoints (#1184)" },
  { name: "tds_review", group: "TDS", description: "Review a TDS draft — approve or request changes; publishing requires a current approval (#1184)" },
  { name: "tds_publish", group: "TDS", description: "Issue an internal TDS revision + PDF. Does NOT control which revision the website shows — that registry is admin-only (#1184)" },
  { name: "tds_template_manage", group: "TDS", description: "Create / revise the TDS layout templates every product uses (#1184)" },
  { name: "tds_bootstrap_import", group: "TDS", description: "Seed a document's historical revision lineage from a legacy extraction — one-time, before native authoring begins (#1187)" },
  { name: "schedule_edit", group: "Schedule", description: "Create/edit/delete bookings on the Schedule board (ScheduleIt mirror). Testing phase: the scheduled ScheduleIt sync overwrites any edits to synced bookings; cai-created bookings persist." },
  { name: "rm_planner_edit", group: "Operations", description: "Edit the raw-material reordering planner config — supplier lead times, safety stock, order rules, and production-run → SKU mappings." },
  { name: "bom_approve", group: "BOM Suite", description: "Approve BOM review changes — ceo_ops/admin hold it by role; grant per-user to add an approver without the whole ceo_ops role (#1169)" },
  { name: "bom_sign_finance", group: "BOM Suite", description: "Sign the Finance seat of a BOM version. A version publishes only once Finance and Ops have both signed (#3222). Grant per user; no role carries it" },
  { name: "bom_sign_ops", group: "BOM Suite", description: "Sign the Ops seat of a BOM version. A version publishes only once Finance and Ops have both signed (#3222). Grant per user; no role carries it" },
  { name: "promote_spec_version", group: "Config", description: "Promote/retire which spec version is in force (#1697)" },
  { name: "mint_qa_product", group: "Config", description: "Create a new QC product card by cloning an existing product (#1579)" },
  { name: "manage_qc_stations", group: "Config", description: "Register, remove and unlock QC stations and set the PIN length (#4447; admin only, revocable per user)" },
  { name: "manage_help_inbox", group: "Config", description: "Answer help@crosbe.ai requests and choose who may email it (#4468; admin only, revocable per user)" },
  { name: "review_ideas", group: "Config", description: "Approve or reject ideas on Updates; approving files a GitHub issue on crosbe-ai (#926; admin only, revocable per user)" },
  { name: "morning_run_rehearse", group: "Config", description: "Rehearse the whole 7 am morning run on the staging site (#4618; admin only, revocable per user; refused on production)" },
  { name: "manage_team_facts", group: "Config", description: "Add, edit and retire the team facts every user's Data Analyst is given, on Admin > Team facts or in chat (#4497; admin only, revocable per user)" },
  { name: "rd_material_costs", group: "R&D", description: "See material costs (per-kg prices) in R&D reads (#1125 — read projection)" },
  { name: "rm_qc_write", group: "QA Entry", description: "Record incoming raw-material test results — panel samples and sieve runs" },
  { name: "rm_qc_admin", group: "QA Entry", description: "Manage raw-material QC materials, tests, panels and limits" },
  { name: "material_mapping_write", group: "Material Costs", description: "Re-point an alias, rename a material, dismiss an unresolved alias (#2731; admin, technical_manager, qc_manager, rd)" },
];

/** Convenient role grants whose enforcement expands to independently grantable units. */
export const WRITE_ACTION_BUNDLES = {
  rdti_log: [
    "rdti_task_write",
    "rdti_gate_walk",
    "rdti_entry_write",
    "rdti_lock_own",
    "rdti_crumb_write",
  ],
  rdti_manage: [
    "rdti_review_others",
    "rdti_project_manage",
    "rdti_clockify_admin",
  ],
} as const satisfies Partial<Record<WriteAction, readonly WriteAction[]>>;

/**
 * #3304: the portable (RBAC v2) capabilities the route gates resolve through
 * hasPortableCapability (role_capability_grants rows, plus the
 * bundle:rdti_broad user override for rdti.log).
 */
export const PORTABLE_CAPABILITIES = ["rdti.log", "rdti.manage"] as const;

export type PortableCapability = (typeof PORTABLE_CAPABILITIES)[number];

/**
 * #3304: which portable capabilities carry write actions, each mapped to the
 * ONE write bundle it stands for. rdti.log carries the rdti_log bundle and its
 * five self-service units, and nothing else. rdti.manage carries no write
 * action through this table (approved scope, 14 Oct: holders of the portable
 * grant log their own time; review-others, project-manage and Clockify admin
 * come only from a role's own allowed_write_actions).
 */
export const PORTABLE_WRITE_CAPABILITIES: Readonly<
  Partial<Record<PortableCapability, keyof typeof WRITE_ACTION_BUNDLES>>
> = {
  "rdti.log": "rdti_log",
};

/** The bundle name plus its units a portable capability carries ([] for none). */
export function portableCapabilityWriteActions(
  capability: PortableCapability,
): WriteAction[] {
  const bundle = PORTABLE_WRITE_CAPABILITIES[capability];
  if (bundle === undefined) return [];
  return [bundle, ...WRITE_ACTION_BUNDLES[bundle]];
}

/**
 * Which portable capability, if any, covers a write action: the one whose
 * bundle IS the action or CONTAINS it. Null for every action outside the
 * rdti_log bundle, so the portable path can never widen an unrelated write.
 */
export function portableCapabilityForWriteAction(
  action: WriteAction,
): PortableCapability | null {
  for (const capability of PORTABLE_CAPABILITIES) {
    if (portableCapabilityWriteActions(capability).includes(action)) {
      return capability;
    }
  }
  return null;
}

export const writeActionOverrideKey = (action: WriteAction): string =>
  `write:${action}`;

// ─── AI Data Access Control ──────────────────────────────────
// Defines which AI tools and database tables each role can access.
// This is the EXECUTION-LEVEL security gate — prompt injection can't bypass it.
//
// Per the top-of-file architecture note: the live data lives in D1, not in
// this file. ROLE_FALLBACK_DEFAULTS below is only consulted when D1 returns
// no row (which means an unrecognised custom role ID, or a built-in role
// without a D1 row), or when D1 throws.

export interface RoleDataAccess {
  /** AI tools this role can use. "*" = all tools. */
  allowed_tools: string[];
  /** #1125: true = members see ONLY research_product families granted in
   *  rd_family_grants. Union: ANY unscoped role → unscoped (union widens). */
  rd_family_scoped?: boolean;
  /** Table name prefixes this role can query via run_sql/list_tables/get_table_schema.
   *  "*" = all tables. Matched as prefix (e.g. "production_monitor_" matches "production_monitor_batch"). */
  allowed_table_prefixes: string[];
  /**
   * Resource-level gate for `query_ostendo`. When set, the AI's query_ostendo
   * tool call is blocked unless `args.resource` is in this list.
   *
   * Deny by default: `isOstendoResourceAllowed` (below) returns true only
   * for a resource explicitly named in this list.
   *
   * `undefined` = no slice configured, so every resource is denied here.
   * `[]` = role has the tool listed but cannot use any resource here.
   *
   * "Here" matters once RBAC v2 is authoritative (#2963): the callers
   * consult two D1-backed escapes after this slice denies, an exact generic
   * `role_ostendo_grants` row (ostendoSliceGenericGrant) and a projected
   * capability (ostendoSliceProjectedGrant), so emptying or removing the
   * slice does NOT revoke a resource the role still holds by grant. Revoke
   * the grant row to revoke access; the slice alone is not the off switch.
   *
   * The one exception is the pre-v2 unsliced-allow path, shared by every
   * consumer through `ostendoResourceVerdict` (#2877): until RBAC v2's
   * capability manifest is live, an undefined slice is treated as allow
   * rather than calling isOstendoResourceAllowed directly, so main's
   * documented unsliced semantics hold there until the flip. Both
   * query-ostendo-handlers.ts (runs the query) and prompt-suggestions.ts
   * (decides whether to resurface a past prompt that used one) call
   * `ostendoResourceVerdict`, never `isOstendoResourceAllowed` directly, so
   * this exception cannot drift between them again.
   *
   * Closes Marcus Jr's GH #94 finding (2026-05-24): the previous binary
   * allow/deny on query_ostendo let R&D pull cost_history / sales_invoices
   * despite the data_restriction_note saying "no cost/pricing access".
   */
  allowed_ostendo_resources?: string[];
  /**
   * #2173: the role id(s) this access was resolved from (a union carries every
   * contributing id). Read by the CROS purchasing gate when the slice is
   * undefined: an unsliced role reaches CROS receipts / purchase_orders /
   * purchase_invoices (#2177) only
   * when one of its ids is in CROS_PURCHASING_UNSLICED_ROLE_IDS. Absent (a
   * bare fallback object) means no id is known, which the gate treats as
   * not allowed.
   */
  role_ids?: string[];
  /**
   * Write/action capabilities this role may perform (#349). `["*"]` = all actions.
   * `undefined` or `[]` = no write access (fail-closed — the safe default for every
   * read-only role and the pre-#349 status quo). Consumed by `isWriteAllowed()` at the
   * write routes (#339). Like `allowed_ostendo_resources`, this is sourced from the
   * hardcoded fallback only for now — the D1 `roles` table has no column for it yet
   * (Phase 2 adds the column + Admin-UI editing). Built-in roles always carry a
   * fallback entry, so the QC rollout works without the column.
   */
  allowed_write_actions?: (WriteAction | "*")[];
  /** Human-readable description of what data this role CANNOT access (injected into system prompt). */
  data_restriction_note: string;
  /**
   * #3266: set only on the access a server-side agent runs on
   * (shared/synthetic-agents.ts). `isToolAllowed` then answers from
   * SYNTHETIC_AGENT_TOOLS alone: no SELF_CONTENT, no derived tools, no "*".
   */
  synthetic_agent?: string;
  /**
   * #4468: a per-call ceiling on a REAL user's access, for a turn nobody typed
   * (an email to help@crosbe.ai). When set, a tool not named here is refused
   * everywhere a tool is decided: `isToolAllowed` (the self-content and
   * run_sql-derived tools included), the page-gated tools in
   * visibleToolDefinitions and at execution, and the Outlook append. It only
   * narrows: a tool named here still needs the user's own grant. Never stored
   * in D1.
   */
  tool_ceiling?: readonly string[];
}

/** #4468: false when the access carries a tool ceiling that does not name this tool. */
export function isWithinToolCeiling(toolName: string, access: Pick<RoleDataAccess, "tool_ceiling">): boolean {
  return access.tool_ceiling === undefined || access.tool_ceiling.includes(toolName);
}

/**
 * Resource → S/P/I/A module taxonomy (per Hugo's GH #94 matrix). Used by
 * isOstendoResourceAllowed and surfaced in system-prompt restriction notes
 * so the AI explains "you have S+I access but tried to query a P resource".
 *
 * If a resource is missing from this map, it's treated as Inventory (safest
 * default — Inventory is the most commonly-allowed module in Hugo's matrix).
 *
 * #922: derived from OSTENDO_RESOURCE_CATALOG (src/shared/ostendo-catalog.ts),
 * the single source of truth that also carries the per-resource DB/label/
 * description metadata the access-mapping explorer renders. Classification
 * notes (inventory_transactions crossing module boundaries, budget carrying
 * revenue + margin, etc.) live on the catalog entries. A parity test pins the
 * catalog against the handler's resource list so neither can drift.
 */
export const OSTENDO_RESOURCE_MODULE: Record<string, "S" | "P" | "I" | "A"> =
  OSTENDO_RESOURCE_MODULE_FROM_CATALOG;

/**
 * Fallback defaults used by `getRoleDataAccessFromD1()` in server/lib/rbac.ts
 * when D1 returns no row or the read fails. NOT the primary access source —
 * the D1 `roles` table is. See top-of-file architecture note.
 *
 * Keys are built-in role IDs: admin, management, rd, qa_tech, viewer, pending
 * (#360), and the tiered QC roles qc_viewer/qc_manager (#349). Custom role IDs
 * (UUIDs from Admin UI) fall through to the `viewer` entry here when not in D1,
 * and so does any id the intended-access spec forbids (#4109): that is the
 * right fail-closed default for an unrecognised or retired role.
 */

/**
 * #360: the safe default for an authenticated user with NO role assignment.
 * Zero data APIs, a single neutral landing page. An unassigned user must never
 * fall back to the broad `viewer` role (which grants lab/cert/analytics reads) —
 * that was the Overview-data leak Hugo flagged. The rbac middleware and
 * /api/rbac/me both resolve an unassigned user to this instead of `is_default`.
 * QC members are pre-provisioned to qc_dashboards (auth.ts signup hook), so they
 * never hit this; genuinely-unprovisioned logins land on /pending and see nothing.
 */
export const PENDING_ROLE = {
  id: "pending",
  name: "Pending — awaiting role assignment",
  allowed_pages: ["/pending"],
  allowed_apis: [] as string[],
};

/**
 * #5004 RDREADALIAS: the live R&D / Technical (read) role, stored on prod and
 * staging under this pre-slug id rather than the `rd_read` slug. The server's
 * fallbackKeyForRole aliases it to the `rd_read` entry below, as it aliases the
 * Sales Manager UUID to `sales_manager`.
 */
export const RD_READ_ROLE_UUID = "113b9302-ced6-48d0-8b7f-423acc41244e";

export const ROLE_FALLBACK_DEFAULTS: Record<string, RoleDataAccess> = {
  admin: {
    allowed_tools: ["*"],
    allowed_table_prefixes: ["*"],
    allowed_write_actions: ["*"],
    data_restriction_note: "",
  },
  management: {
    allowed_tools: ["*"],
    allowed_table_prefixes: ["*"],
    // #339 (Hugo): management gets NO QC write actions — it's a read/analysis
    // tier with pages/apis ["*"], so a write "*" would let every management user
    // (incl jezweb accounts on this role) enter/edit/delete QC samples + edit spec
    // configs once the write routes land. QC writes stay qc_analyst/qc_manager + admin.
    // #513: management DOES get `kb_write` (knowledge-base curation is a management
    // task, "kb manager and above") — a SPECIFIC action, not "*", so it grants KB
    // create/edit/delete WITHOUT re-opening the QC-sample write actions above.
    // #585: management also authors Commercial Hub notes (it holds the Hub page via
    // allowed_pages ["*"]) — a SPECIFIC action, not a QC-write re-open.
    // #741: management curates its own Projects content (beats/concerns/evidence) —
    // it already reaches /api/projects via pages/apis ["*"] and can own a project;
    // project_manage is a SPECIFIC action, consistent with the no-"*"-write policy.
    // #587: management may ASK a Troubleshooting question (it already reaches the
    // /commercial/troubleshooting page via allowed_pages ["*"]). Review stays a
    // Technical Services capability (technical_services + admin) — a SPECIFIC add,
    // consistent with the no-"*"-write policy.
    // #1567: management edits RM-planner config (lead times / safety stock /
    // run→SKU mapping — supply planning, not QC data). The grant SQL's NULL-guard
    // deliberately skips this role (its D1 write-actions row is NULL so it
    // inherits THIS list; an explicit D1 list would freeze it), so the grant
    // lives here. Another SPECIFIC action, not a QC-write re-open.
    // #2159 / #3397: management does NOT hold write_pipeline_deal. Akira ruled on
    // 8 Sep 2026 that the Sales Pipeline is sales_manager, sales_rep and admin only
    // (see the WriteAction union above); management reads the board via
    // allowed_pages ["*"] and curates nothing on it. tests/commercial-pipeline-gating
    // pins the refusal; tests/rbac-fallback-comment-3397 pins this comment against the list.
    // #2056: management opens cases, logs visits and manages cases (it reaches
    // /commercial/cases via allowed_pages ["*"]). The grant lives HERE because
    // migrate-cases-02-grants.sql skips this role (D1 row NULL → this list).
    allowed_write_actions: [
      "kb_write", "write_commercial_note", "project_manage", "troubleshooting_ask", "rm_planner_edit",
      "case_open", "visit_log", "case_manage",
    ],
    data_restriction_note: "",
  },
  // #374 / #585: ceo_ops (Albert, Andrew) — the CEO/Operations role that drives the
  // Commercial dashboard. Its pages/APIs live in the D1 roles row (seeded separately);
  // this fallback entry exists so its WRITE action resolves (write actions come from
  // the fallback only — no D1 column yet, per the #349 note above). AI-off + no
  // table access on the outage path (conservative, like technical_manager). #585:
  // ceo_ops may author Commercial Hub notes (ANY hub-access role — Hugo's call).
  ceo_ops: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    // #587: ceo_ops is the commercial/rep-capable role — it may ASK Troubleshooting
    // questions (guided + chat) from the mobile rep surface. Review is a separate
    // Technical Services capability (technical_services role), so ceo_ops does NOT
    // get troubleshooting_review.
    // #2056: ceo_ops opens cases and logs visits (the rep-capable commercial role).
    // case_manage stays with Technical Services.
    // COMPPRICE-AUD (Akira, 29 Sep 2026): ceo_ops logs competitor prices like a
    // rep (delete its own only); not delete_any_competitor_price.
    // ANDREWPIPE (Akira, 1 Oct 2026): ceo_ops adds, edits and ticks Sales Pipeline
    // deals, and write_pipeline_deal also lets it soft-delete ANY deal (the DELETE
    // route has no author check). Clearing the whole board (reset_pipeline_board)
    // stays with Sales Manager.
    // RBACDRIFT2 (#3979; Akira, 14 Oct 2026: the prod row decides): the prod
    // ceo_ops row holds schedule_edit, rm_planner_edit and bom_approve, as the
    // spec does, so the fallback carries them too. bom_approve stays exactly as
    // prod holds it; the open question on removing it (#2808) is not settled here.
    allowed_write_actions: [
      "write_commercial_note", "troubleshooting_ask", "case_open", "visit_log", "write_competitor_price", "write_pipeline_deal",
      "schedule_edit", "rm_planner_edit", "bom_approve",
    ],
    data_restriction_note:
      "CEO/Operations: the commercial dashboards plus the pages/APIs granted in the roles store. No AI Data Analyst during store outages.",
  },
  // The one real Sales Manager holds a pre-slug UUID role (#1118), whose D1 row
  // has allowed_write_actions NULL — i.e. "use this fallback". It reaches this
  // entry through the SALES_MANAGER_ROLE_IDS alias in server/lib/rbac.ts
  // (fallbackForRole); drop that alias and the role silently loses every write.
  sales_manager: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    // #2056: the live Sales Manager row (859306c9…) is in the grant SQL's IN list,
    // but its allowed_write_actions is NULL so the NULL-guarded UPDATE skips it
    // and THIS list is what it resolves. Carry case_open + visit_log here so the
    // grant reaches the role whichever state the row is in.
    // #3327: reset_pipeline_board replaces the reset route's role-id gate; the
    // explicit rows gain it through migrate-3327-pipeline-reset-grant.sql.
    // COMPPRICE #3571: the manager logs competitor prices and may delete any
    // entry; explicit rows gain both through migrate-3571-competitor-prices.sql.
    allowed_write_actions: ["write_commercial_note", "troubleshooting_ask", "write_pipeline_deal", "case_open", "visit_log", "reset_pipeline_board", "write_competitor_price", "delete_any_competitor_price"],
    data_restriction_note:
      "Sales Manager: the commercial dashboards plus the pages/APIs granted in the roles store. No extra data access during store outages.",
  },
  sales_rep: {
    // #1320 v1 (Akira greenlit 2 Aug); sales-roles-polish (captain decisions,
    // Fri 2026-08-07 rollout) removed search_kb: no audience-classification
    // enforcement exists in either the KB API or search_kb, so per decision 1
    // reps lose KB access rather than see unfiltered articles (financial/cost
    // content included). Also NO run_sql / query_ostendo_cros, EVER: arbitrary
    // SQL cannot be row-scoped, and a rep must never reach another rep's
    // customers (#481). Customer-scoped CROS access arrives as a purpose-built
    // server-filtered tool (v2, after #481) — never by widening this list.
    // Matches the D1 store grant in migrate-sales-roles-polish-2026-08-07.sql.
    allowed_tools: [
      "get_product_info", "get_spec_limits",
      "compare_competitor_product", "calculate", "create_chart",
    ],
    allowed_table_prefixes: [],
    // #2056: reps open cases and log site visits from the field. No case_manage.
    // COMPPRICE #3571: reps log competitor prices and delete their own; deleting
    // another rep's entry is delete_any_competitor_price, which they do not hold.
    allowed_write_actions: ["write_commercial_note", "troubleshooting_ask", "write_pipeline_deal", "case_open", "visit_log", "write_competitor_price"],
    data_restriction_note:
      "Sales Representative: product-knowledge AI Analyst (product info, spec limits, competitor equivalence) — no Knowledge Base, no plant-wide QA Insights, no customer or sales data through the Analyst. Commercial dashboards per the roles store.",
  },
  // RBACDRIFT2 (#3979): finance. Before this entry the slug had none and
  // resolved to viewer, which holds no writes. The list is the spec's finance
  // writes, every one of which the prod row holds (Akira, 14 Oct 2026: the prod
  // row decides). bom_approve stays exactly as prod holds it; the open question
  // on removing it (#2808) is not settled here. AI-off and no table access on
  // the fallback path, like ceo_ops.
  finance: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    allowed_write_actions: ["schedule_edit", "rm_planner_edit", "bom_approve"],
    data_restriction_note:
      "Finance: the finance dashboards plus the pages/APIs granted in the roles store. No AI Data Analyst during store outages.",
  },
  rd: {
    allowed_tools: [
      "list_tables", "get_table_schema", "run_sql", "calculate",
      "get_spec_limits", "search_kb", "get_product_info", "get_product_tds", "compare_competitor_product", "create_chart",
      "get_production_sequence", "calculate_process_stats", "get_recent_failures",
      "calculate_carbon",
    ],
    allowed_table_prefixes: [
      // crosbe_ (CRM + NATA-Lab) dropped 2026-06-10 (#338 / Hugo): rd reads LIMS
      // analytics only, not CRM. Aligned with the qc_dashboards-seed D1 migration.
      "production_monitor_", "qa_production_", "raw_materials_",
      "research_", "calibration_",
    ],
    // GH #290 / Akira 2026-06-03: rd has NO Ostendo access — neither
    // query_ostendo_coop nor query_ostendo_cros. LIMS Postgres analytics only.
    // Supersedes the earlier #94 inventory+assembly-only grant: the role was
    // re-scoped to LIMS-only when the dual-DB tool-split landed (the #178 COOP
    // grant was stripped). No allowed_ostendo_resources needed — no Ostendo tool.
    // #502 / Hugo 2026-06-25: rd (+ admin) may write R&D mix designs. Gated at the
    // /api/rd/designs* write routes via requireWriteAction("mixdesign_write").
    // #740/#741: rd runs the project programs — grant project_manage (its Projects
    // write action). Carried in fallback for deploy-order safety even though
    // migrate-projects-02 also seeds the D1 allowed_write_actions column.
    // #789: rd (+ technical_manager + admin) may create/edit R&D ingredients
    // (research_rawmaterial) natively — legacy Django-admin parity. Gated at the
    // /api/rd/materials write routes via requireWriteAction("edit_rd_ingredient").
    // #815: rd (+ technical_manager + admin) authors production formulations —
    // crosbe-app (Django admin) is being retired; /formulations is the permanent
    // home. Gated at the /api/formulations write routes via
    // requireWriteAction("formulation_write"). Same roles that read the page today.
    // #1184: rd authors and reviews TDS drafts (the technical team writes the
    // data sheets). Publishing and template management sit with
    // technical_manager — an rd author's work always crosses a second gate.
    allowed_write_actions: [
      "rd_material_costs", // #1125 A
      "mixdesign_write",
      "project_manage",
      "edit_rd_ingredient",
      "formulation_write",
      "rdti_log",
      "rdti_task_write",
      "rdti_gate_walk",
      "rdti_entry_write",
      "rdti_lock_own",
      "rdti_crumb_write",
      "tds_author",
      "tds_review",
      // #2056: rd opens cases + logs visits (technical field support). Mirrored
      // in migrate-cases-02-grants.sql for the explicit D1 row.
      "case_open",
      "visit_log",
      // #2731: rd re-points aliases, renames materials, dismisses unresolved
      // aliases. Mirrored in migrate-2731-mapwrite-grants.sql for the explicit
      // D1 row (#1748 mirror convention).
      "material_mapping_write",
    ],
    data_restriction_note:
      "You do NOT have access to material cost/pricing data, artifact generation, or either Ostendo database (COOP or CROS). Your data access is the LIMS Postgres analytics DB only (R&D, QC, Production Monitor).",
  },
  // RBACDRIFT2 (#3979): R&D / Technical (read). Before this entry the slug had
  // none and resolved to viewer, which holds no writes. Akira, 29 Sep 2026
  // (RDREADWRITES): "(read) is a misnomer: it's just a slightly different
  // version of the R&D role. Keep its write actions." The list is the spec's
  // rd_read writes, every one of which the prod row holds (Akira, 14 Oct 2026:
  // the prod row decides). The live role is stored under the pre-slug id
  // RD_READ_ROLE_UUID, which fallbackKeyForRole aliases to this entry (#5004),
  // so this entry serves both that id and a role stored under the rd_read slug.
  // AI-off and no table access on the fallback path, like technical_manager.
  rd_read: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    allowed_write_actions: [
      "mixdesign_write",
      "project_manage",
      "edit_rd_ingredient",
      "formulation_write",
      "rdti_log",
      "rdti_task_write",
      "rdti_gate_walk",
      "rdti_entry_write",
      "rdti_lock_own",
      "rdti_crumb_write",
      "case_open",
      "visit_log",
      "tds_author",
      "tds_review",
      "tds_publish",
      "tds_bootstrap_import",
      "tds_template_manage",
    ],
    data_restriction_note:
      "R&D / Technical (read): R&D, TDS and RDTI writes plus the pages/APIs granted in the roles store. No AI Data Analyst during store outages.",
  },
  // #727 (Akira's (ii) call, 3 Jul): technical_manager may write R&D mix designs.
  // The D1 roles row + Mark's assignment have existed since 2026-05-28 — but write
  // actions resolve from THIS fallback only (no D1 column yet, fail-closed for
  // roles without an entry), so the grant is this code entry. Tools/prefixes kept
  // conservative: on the normal path they come from the D1 row; this fallback only
  // governs a D1 outage.
  technical_manager: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    // #789: technical_manager also creates/edits R&D ingredients (parity with rd).
    // #815: technical_manager also authors production formulations (parity with rd).
    // #392: technical_manager manages lab equipment + calibration records.
    // 2026-07-29: technical_manager self-serves RDTI tasks, own time, locks, and crumbs.
    // #1184: technical_manager holds the full TDS set — authors, reviews,
    // issues revisions, and manages the layout templates. Issuing is the
    // INTERNAL head only; the website-official registry stays admin-only.
    // #1187: bootstrap-importing a document's historical lineage sits here
    // too, not with rd — it is a one-time, compliance-sensitive act (mislabel
    // a legacy extraction as native and it is indistinguishable from an
    // author's own verified work), same posture as publish/template-manage.
    allowed_write_actions: [
      "rd_material_costs", // #1125 A (parity with rd)
      "mixdesign_write",
      "edit_rd_ingredient",
      "formulation_write",
      "edit_equipment",
      "rdti_log",
      "rdti_task_write",
      "rdti_gate_walk",
      "rdti_entry_write",
      "rdti_lock_own",
      "rdti_crumb_write",
      "tds_author",
      "tds_review",
      "tds_publish",
      "tds_template_manage",
      "tds_bootstrap_import",
      // #2056: technical_manager opens cases, logs visits and manages cases
      // (Akira, 2026-09-04 — case_manage alongside technical_services). Mirrors
      // migrate-cases-02-grants.sql; the D1 row is explicit so the SQL is what
      // grants it live — this entry keeps fallback ↔ D1 parity (the #1184b lesson).
      "case_open",
      "visit_log",
      "case_manage",
      // #2731: technical_manager re-points aliases, renames materials,
      // dismisses unresolved aliases. Mirrored in
      // migrate-2731-mapwrite-grants.sql for the explicit D1 row (#1748
      // mirror convention).
      "material_mapping_write",
      // RBACDRIFT2 (#3979; Akira, 14 Oct 2026: the prod row decides): the prod
      // technical_manager row holds project_manage, as the spec does (parity with rd).
      "project_manage",
    ],
    data_restriction_note:
      "Technical Manager: R&D mix-design editing plus the pages/APIs granted in the roles store. No AI Data Analyst during store outages.",
  },
  qa_tech: {
    allowed_tools: [
      "list_tables", "get_table_schema", "run_sql", "calculate",
      "get_spec_limits", "search_kb", "get_product_info", "get_product_tds", "compare_competitor_product", "create_chart",
      "get_production_sequence", "calculate_process_stats", "get_recent_failures",
    ],
    allowed_table_prefixes: [
      // crosbe_ (CRM + NATA-Lab) dropped 2026-06-10 (#338 / Hugo): qa_tech reads
      // QC + production analytics only, not CRM. Aligned with the D1 migration.
      "production_monitor_", "qa_production_", "raw_materials_",
      "calibration_",
    ],
    // QCALARMREACH (#3792; Akira, 2 Oct 2026: "QC technician not analyst"):
    // qa_tech holds /production (production_read) and sets and clears the
    // Batch-Count Alarm there. Mirrored in migrate-3792-qatech-set-batch-alarm.sql
    // for an explicit D1 list (#1748 mirror convention). qc_manager keeps its own.
    // RBACDRIFT (#3801; Akira, 6 Oct 2026: QC technicians keep every action
    // their prod row holds, "that's their whole job"): this list equals that
    // row and the spec's qa_tech writes. tests/rbac/fallback-writes-match-spec-3801
    // pins fallback writes to the spec for every spec role.
    // #2283 APPRMON: release_batch (Akira, 10 Oct 2026), D1 row in
    // migrate-2283b-release-batch-grants.sql.
    allowed_write_actions: ["enter_sample", "edit_sample", "set_approval", "rm_qc_admin", "rm_qc_write", "set_batch_alarm", "release_batch"],
    data_restriction_note:
      "You do NOT have access to cost/pricing data, R&D formulations, carbon footprint data, or artifact generation. Do not attempt to query this information.",
  },
  // qc_dashboards, qc_analyst and technical_services were retired by Akira in
  // User Management on 30 Sep 2026 (#3995) and have no entry here (#4109): an
  // id the spec's forbidden_role_ids names resolves to viewer, so a hand-run
  // insert under the literal id inherits no writes.
  // tests/rbac/no-fallback-for-forbidden-4109 pins that.
  viewer: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    data_restriction_note:
      "You do not have access to the AI Data Analyst. Please ask your administrator for access.",
  },
  // #845 (option 1): read-only verification role for marcus+test* users. Pages/APIs are
  // seeded in D1 (migrate-845-test-readonly-role.sql — cloned from rd, a COSTS-safe read
  // config). This fallback pins the data-access dimension explicitly: NO AI tools, NO AI-SQL,
  // NO writes — a strict subset of rd, so it can never leak more than rd already can.
  test_readonly_all: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    allowed_write_actions: [],
    data_restriction_note:
      "Read-only verification account. No AI Data Analyst, no run_sql, no AI-SQL table access, no writes, no material cost/pricing data.",
  },
  // BOM-suite verification lane (Jez 6 Aug). Separate from test_readonly_all
  // because that role's #845 charter is "no costs data" — this one reads BOM
  // costs, and only that. Capability dimension stays empty so a NULL-column
  // D1 row can never widen it.
  test_bom_readonly: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    allowed_write_actions: [],
    data_restriction_note:
      "BOM verification account: read the BOM Suite only. No AI tools, no SQL, no writes, no proposing/approving.",
  },
  // #360: unassigned users — AI fully off, no data, matches PENDING_ROLE.
  pending: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    data_restriction_note:
      "Your account is awaiting role assignment. You have no data access yet — please contact your administrator.",
  },
  // ─── Tiered QC roles (#349) ───────────────────────────────
  // qc_viewer is AI-OFF (allowed_tools []/prefixes []); qc_manager carries the
  // mirrored prod tool list (#1748). They share the QC page/API base (seeded in
  // D1, see migrate-qc-tiered-roles-seed.sql) and differ in allowed_write_actions.
  // That IS the spec's point: the page grant is shared; the WRITE capability is
  // what's tiered. (Akira's divergence from crosbe-app: spec-config editing is
  // manager-only here, not analyst.) The middle tier, qc_analyst, was retired
  // on 30 Sep 2026 (#3995).

  // qc_viewer: read QC dashboards + samples. No write.
  qc_viewer: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    allowed_write_actions: [],
    data_restriction_note:
      "QC Viewer: read-only QC dashboards and samples. No data entry, no AI Data Analyst.",
  },
  // qc_manager: templates/spec-configs (#348) + approval + delete on top of entry.
  // #1748: this fallback MUST mirror the live D1 grant — a D1 read failure
  // serves THIS, and for the platform's two heaviest analyst users a stale
  // empty list here silently presented an outage as "you never had AI access".
  // Synced to the prod row 2026-08-21; when a built-in role's D1 grant is
  // edited, mirror it here in the same change (the drift guard test pins the
  // tool COUNT so a D1-only edit fails loudly at the next sync check).
  qc_manager: {
    allowed_tools: [
      "create_chart", "create_artifact", "export_data",
      "save_memory", "list_memories", "forget_memory",
      "calculate_process_stats", "calculate", "aggregate_data", "pivot_data",
      "compare_groups", "trend_analysis", "distribution", "batch_analysis",
      "get_recent_failures", "get_spec_limits", "get_production_sequence",
      "resolve_product_identity", "resolve_material_identity", "list_tables", "get_table_schema", "run_sql",
      "read_data", "get_product_info", "business_lookup", "read_webpage",
      "web_search", "competitor_profile", "trace_order",
      "compare_competitor_product", "get_product_tds", "trace_recall", "trace_material",
    ],
    allowed_table_prefixes: ["production_monitor_", "qa_production_", "raw_materials_", "crosbe_", "calibration_"],
    allowed_write_actions: [
      "enter_sample", "edit_sample", "set_approval", "delete_sample",
      "release_batch", // #2283 APPRMON (Akira, 10 Oct 2026); D1 row in migrate-2283b-release-batch-grants.sql
      "cancel_sample", "restore_sample", // FGCANCEL #4857 (Akira, 2 Nov 2026: QC manager only); D1 row in migrate-4857-sample-cancel-grants.sql
      "edit_template", "edit_spec_config", "edit_test_types", "set_batch_alarm", "promote_spec_version", "mint_qa_product",
      "create_upi", "edit_upi", "assign_upi",
      "edit_equipment", // #392: lab equipment + calibration management
      "rm_qc_write", // RM QC prototype: panel + sieve entry
      "rm_qc_admin", // #2256: RM-QC vocabulary and panel manager
      "case_open", "visit_log", // #2056: QC manager opens cases + logs visits (batch-linked field issues)
      "material_mapping_write", // #2731: re-point aliases, rename materials, dismiss unresolved aliases (#1748 mirror convention; D1 row in migrate-2731-mapwrite-grants.sql)
      // #3740 TDSSTD: open QC vs TDS and edit the standard behind each TDS
      // claim. D1 row in migrate-3740-tds-claim-standards-grants.sql (HELD).
      "view_qc_vs_tds", "edit_tds_claim_standards",
    ],
    data_restriction_note:
      "QC Manager: full QC entry plus template/spec-limit editing, approval, and delete.",
  },
  // #482 Tech Services (Salim, Tami): the commercial views — customer, product, volume,
  // delivery location, QC — but NO pricing/revenue/margin/contribution. Dollars are
  // stripped server-side in the commercial handlers (lib/monetary-strip), enforced at
  // the API not the UI. AI-OFF like the dashboard roles; page/API grants seeded in D1.
  tech_services: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    // #585: tech_services holds the Commercial Hub page, so it may author Hub notes
    // (ANY hub-access role — Hugo's call). Note authorship carries no $, so this
    // doesn't interact with the monetary strip.
    // #2056: tech_services (Salim, Tami today) opens cases + logs visits. Their
    // case_manage is a PER-USER override (Admin → Users), never a role grant —
    // this role is shared with Nathan and Adam. Cases responses never carry $ keys.
    // #2545: view_qc_vs_tds opens QC vs TDS (Akira, 24 Sep 2026). The explicit
    // row gains it through migrate-2545-qc-vs-tds-grants-tech-services.sql.
    // #3740: edit_tds_claim_standards edits the standard behind each TDS claim
    // on its Claims vs tests tab; migrate-3740-tds-claim-standards-grants.sql.
    allowed_write_actions: ["write_commercial_note", "case_open", "visit_log", "view_qc_vs_tds", "edit_tds_claim_standards"],
    data_restriction_note:
      "Tech Services: commercial views (customer, product, volume, delivery location, QC) with all pricing, revenue, margin and contribution removed server-side. No AI Data Analyst.",
  },
  // #703 RDTI Time Tracker: R&D staff logging claimable time + managing the
  // RDTI project/task register. AI-OFF like the dashboard roles (tools []/
  // prefixes []). Page/API grants live in the D1 roles row (see
  // .jez/scripts/migrate-rdti-role-seed.sql — allowed_apis ["/api/rdti"],
  // allowed_pages ["/rdti"], applied 22 Jul 2026); write actions resolve from
  // this fallback (no D1 column yet, same as every role above). Ownership of
  // entries (edit OWN only) is enforced at the /api/rdti/entries routes, not here.
  rdti: {
    allowed_tools: [],
    allowed_table_prefixes: [],
    allowed_write_actions: [
      "rdti_log",
      "rdti_manage",
      "rdti_task_write",
      "rdti_gate_walk",
      "rdti_entry_write",
      "rdti_lock_own",
      "rdti_crumb_write",
      "rdti_review_others",
      "rdti_project_manage",
      "rdti_clockify_admin",
    ],
    data_restriction_note:
      "RDTI: R&D time tracking — own entries + project/task management. No AI Data Analyst.",
  },
};

// ─── #737 multi-role union semantics ─────────────────────────
// Many Crosbe staff wear multiple hats (Mark Townsend qc_analyst + management
// read-breadth; Tamires tech_services + qc_analyst; ops/accounts base role +
// view-only add-ons). The D1 schema always allowed multiple user_roles rows per
// user, but resolution picked exactly ONE by a hardcoded precedence ladder.
// #737: effective permissions = UNION across ALL assigned roles, per dimension
// (pages, apis, tools, table_prefixes, write_actions). Zero rows still = pending
// (#360, unchanged). Single-role users are unaffected (union of one = itself).

/**
 * Union a set of grant lists (pages / apis / tools / table prefixes / write
 * actions). A "*" wildcard in ANY list collapses the union to ["*"] — a
 * wildcard role plus anything is wildcard-equivalent (admin + x = admin).
 * Union of zero lists is [] (fail-closed).
 */
export function unionGrantLists(lists: string[][]): string[] {
  if (lists.some((l) => l.includes("*"))) return ["*"];
  const out = new Set<string>();
  for (const l of lists) for (const v of l) out.add(v);
  return [...out];
}

/**
 * #4949: a copy of `access` that shares no array with it, with exactly its
 * keys. Every path that hands out a ROLE_FALLBACK_DEFAULTS entry, a module
 * constant or another caller's access returns this, so whatever the receiver
 * changes stays on its own object. #4953: a code-defined prefix list (its "="
 * exact-table entries honoured through an identity-keyed brand, see
 * shared/table-grants.ts) is re-minted by codeDefinedGrants, so the copy
 * keeps that authority and stays frozen; any other list (one read from D1)
 * is a plain copy and never gains it.
 */
export function copyRoleDataAccess(access: RoleDataAccess): RoleDataAccess {
  const { allowed_ostendo_resources, allowed_write_actions, role_ids, tool_ceiling } = access;
  const prefixes = access.allowed_table_prefixes;
  return {
    ...access,
    allowed_tools: [...access.allowed_tools],
    allowed_table_prefixes: honoursExactGrants(prefixes) ? codeDefinedGrants(prefixes) : [...prefixes],
    ...(allowed_ostendo_resources ? { allowed_ostendo_resources: [...allowed_ostendo_resources] } : {}),
    ...(allowed_write_actions ? { allowed_write_actions: [...allowed_write_actions] } : {}),
    ...(role_ids ? { role_ids: [...role_ids] } : {}),
    ...(tool_ceiling ? { tool_ceiling: [...tool_ceiling] } : {}),
  };
}

/**
 * #737: union RoleDataAccess across a user's assigned roles.
 *
 * Per-dimension semantics:
 *  - `allowed_tools` / `allowed_table_prefixes`: plain union ("*" collapses).
 *  - `allowed_write_actions`: a role with undefined/[] contributes nothing;
 *    if NO role grants any write action the union is undefined (fail-closed,
 *    identical to the single-role behaviour `isWriteAllowed` already pins).
 *  - `allowed_ostendo_resources`: `undefined` means "no per-resource gate" —
 *    but ONLY roles that actually hold an Ostendo tool contribute here. A
 *    tool-less role's `undefined` must NOT read as "all resources" once the
 *    union grants the tool from a sibling role (that would silently drop the
 *    per-resource restriction of the role that brought the tool). If no
 *    contributing role exists the gate unions to [] (fully blocked — the tool
 *    gate already denies, this is belt-and-braces fail-closed).
 *  - `data_restriction_note`: join distinct non-empty notes (the #737-resolved
 *    "join + dedupe, simplest honest option").
 *
 * Union of zero accesses = a copy of the viewer fallback (fail-closed).
 * Union of one access = a copy equal to that access (single-role users
 * unaffected). #4949: never the object it was given, which can be a shared
 * ROLE_FALLBACK_DEFAULTS entry the caller goes on to change.
 */
export function unionRoleDataAccess(accesses: RoleDataAccess[]): RoleDataAccess {
  if (accesses.length === 0) return copyRoleDataAccess(ROLE_FALLBACK_DEFAULTS.viewer);
  if (accesses.length === 1) return copyRoleDataAccess(accesses[0]);
  // #3266: a synthetic agent access is never unioned. The result below would
  // drop the tag and hand the agent back SELF_CONTENT inheritance.
  const synthetic = accesses.find((a) => a.synthetic_agent !== undefined);
  if (synthetic) {
    throw new Error(`Synthetic agent access (${synthetic.synthetic_agent}) cannot be unioned with other access`);
  }

  const hasOstendoTool = (a: RoleDataAccess) =>
    a.allowed_tools.includes("*") ||
    a.allowed_tools.some((t) => t === "query_ostendo" || t.startsWith("query_ostendo_"));
  const ostendoContributors = accesses.filter(hasOstendoTool);
  let allowed_ostendo_resources: string[] | undefined;
  if (ostendoContributors.length === 0) {
    allowed_ostendo_resources = [];
  } else if (ostendoContributors.some((a) => a.allowed_ostendo_resources === undefined)) {
    allowed_ostendo_resources = undefined;
  } else {
    allowed_ostendo_resources = [
      ...new Set(ostendoContributors.flatMap((a) => a.allowed_ostendo_resources as string[])),
    ];
  }

  const writeLists = accesses
    .map((a) => a.allowed_write_actions ?? [])
    .filter((l) => l.length > 0);
  const allowed_write_actions =
    writeLists.length === 0
      ? undefined
      : (unionGrantLists(writeLists as string[][]) as (WriteAction | "*")[]);

  // #1125: family scoping survives the union only when EVERY role carries it —
  // one unscoped role means full visibility, consistent with union-widens.
  const rd_family_scoped = accesses.every((a) => a.rd_family_scoped === true);

  const notes = [
    ...new Set(accesses.map((a) => a.data_restriction_note.trim()).filter(Boolean)),
  ];

  const role_ids = [...new Set(accesses.flatMap((a) => a.role_ids ?? []))];
  return {
    allowed_tools: unionGrantLists(accesses.map((a) => a.allowed_tools)),
    allowed_table_prefixes: unionGrantLists(accesses.map((a) => a.allowed_table_prefixes)),
    allowed_write_actions,
    allowed_ostendo_resources,
    ...(role_ids.length ? { role_ids } : {}),
    rd_family_scoped,
    data_restriction_note: notes.join(" "),
  };
}

// ─── #423 per-user feature overrides ─────────────────────────
// Role baseline + a thin per-person exception layer (Akira-confirmed design,
// 2026-06-16): effective = role_grants ∪ user_grants − user_revokes, where a
// per-user REVOKE wins even over a wildcard/all-access role (wildcard minus one
// slice — the "Nathan: Admin/all − revoke Costs" anchor). The unit is a feature
// (an individual page/view, or an /api/* prefix) — feature_key reuses the
// path keys RBAC already understands, matched with the same prefix + #719
// alias semantics as isPathAllowed. Resolved SERVER-SIDE only; stored in the
// D1 `user_feature_overrides` table (see .jez/scripts/migrate-423-user-feature-overrides.sql).

export interface UserFeatureOverrides {
  /** Feature keys granted on top of the role baseline. */
  grants: string[];
  /** Feature keys revoked — these win over EVERYTHING, including "*". */
  revokes: string[];
}

/** The no-overrides value — resolution degrades to the pure role baseline. */
export const NO_OVERRIDES: UserFeatureOverrides = { grants: [], revokes: [] };

/** Prefix + alias-aware match of a path against a set of override feature keys. */
function matchesFeatureKey(
  path: string,
  keys: string[],
  aliases: Record<string, string>
): boolean {
  if (keys.length === 0) return false;
  const p = canonicalPath(path, aliases);
  return keys.some((k) => {
    const ck = canonicalPath(k, aliases);
    return p === ck || p.startsWith(ck + "/");
  });
}

/**
 * #423 resolution: `effective = role_grants ∪ user_grants − user_revokes`.
 *
 * Order is load-bearing:
 *  1. REVOKE first — a per-user revoke denies even when the role list is ["*"]
 *     (revoke-wins-even-over-wildcard, Akira-confirmed) and even when the same
 *     key somehow also appears as a grant (contradictory data resolves closed).
 *  2. GRANT next — a per-user grant adds on top of the role baseline.
 *  3. Role baseline last — no overrides ⇒ identical to isPathAllowed (the
 *     pre-#423 behaviour, which is also the pre-migration fallback).
 */
export function isPathAllowedWithOverrides(
  path: string,
  allowedPrefixes: string[],
  overrides: UserFeatureOverrides,
  aliases: Record<string, string> = PATH_ALIASES
): boolean {
  if (matchesFeatureKey(path, overrides.revokes, aliases)) return false;
  if (matchesFeatureKey(path, overrides.grants, aliases)) return true;
  return isPathAllowed(path, allowedPrefixes, aliases);
}

/**
 * #3304 round: whether a per-user revoke covers this path (same prefix and
 * alias match as isPathAllowedWithOverrides). An admit that bypasses the role
 * list, like the portable RDTI capability, still loses to a user revoke.
 */
export function isPathRevokedByOverride(
  path: string,
  overrides: UserFeatureOverrides,
  aliases: Record<string, string> = PATH_ALIASES
): boolean {
  return matchesFeatureKey(path, overrides.revokes, aliases);
}

/**
 * Tools that operate on content the USER THEMSELVES put into (or built inside)
 * this conversation (#1444: re-reading their own uploads; #1235: reports the
 * analyst assembles FOR them, owner-scoped in D1). These are not data-access
 * grants — the role tool list protects CROSBE data, and every value inside a
 * report already passed the per-role gates of the tool that produced it — so
 * they are available to every AI-on role WITHOUT a per-role D1 rollout.
 * Deliberate: adding a tool name to every captain-managed live role row is
 * exactly the kind of live-row surgery #1423 taught us not to do casually.
 * AI-off roles (empty tool list) still get nothing.
 */
export const SELF_CONTENT_TOOLS: ReadonlySet<string> = new Set([
  "read_attachment",
  // #2090: row ranges of the user's OWN catalogued upload — same scope as read_attachment.
  "read_attachment_range",
  "create_report",
  "list_my_reports",
  "get_report",
  "edit_report",
  // #1808(b): every AI-on role can FILE an access request — the tool writes
  // only the caller's own request row, so it is self-content by construction.
  "request_access",
  // #1930: reads the caller's OWN connected mailbox — per-user gate (connection
  // row) replaces the role gate; visible only while a connection exists.
  "outlook_read",
]);

/**
 * #2114: tools that only package a query the role could already write with
 * run_sql over the listed tables. Allowed when granted by name OR when run_sql
 * is granted AND at least one of the tables passes isTableNameAllowed — the
 * same per-table predicate run_sql applies, so a role whose reach is one
 * narrow prefix (`qa_production_ptgrout`) qualifies exactly as far as run_sql
 * would let it, and no new data becomes reachable. No per-role grant (and no
 * D1 migration) is needed. The handler filters its catalogue by the same
 * predicate; an explicit grant with nothing in reach is "no products", not
 * "denied". Static catalogue only here (shared code cannot read the dynamic
 * registry); the handler applies the predicate to registry products too.
 */
export const RUN_SQL_DERIVED_TOOLS: Readonly<Record<string, readonly string[]>> = {
  count_tests: Object.values(QA_PRODUCTS).map((p) => p.sample),
};

/** #2114: the derived-grant predicate, shared by isToolAllowed and the handler. */
export function isRunSqlDerivedToolAllowed(toolName: string, access: RoleDataAccess): boolean {
  if (!Object.hasOwn(RUN_SQL_DERIVED_TOOLS, toolName)) return false;
  const tables = RUN_SQL_DERIVED_TOOLS[toolName];
  return tables !== undefined
    && access.allowed_tools.includes("run_sql")
    && tables.some((t) => isTableNameAllowed(t, access.allowed_table_prefixes));
}

/** Check if a tool is allowed for the given data access config. */
export function isToolAllowed(toolName: string, access: RoleDataAccess): boolean {
  // #3266: a synthetic agent gets exactly its table row, default deny.
  if (access.synthetic_agent !== undefined) {
    return syntheticAgentTools(access.synthetic_agent).includes(toolName);
  }
  if (!isWithinToolCeiling(toolName, access)) return false;
  if (access.allowed_tools.includes("*")) return true;
  if (SELF_CONTENT_TOOLS.has(toolName)) return access.allowed_tools.length > 0;
  if (access.allowed_tools.includes(toolName)) return true;
  return isRunSqlDerivedToolAllowed(toolName, access);
}

/**
 * #349: check if a write/action capability is allowed. Fail-CLOSED — a role with
 * no `allowed_write_actions` (undefined, the pre-#349 default for every existing
 * role) can perform no writes. `["*"]` allows all actions (admin/management).
 * Call this at every write route (#339) alongside the path/API gate: holding the
 * page is necessary but not sufficient — the action itself must be permitted.
 */
function bundleIncludes(bundle: WriteAction, action: WriteAction): boolean {
  if (!Object.hasOwn(WRITE_ACTION_BUNDLES, bundle)) return false;
  const units = WRITE_ACTION_BUNDLES[bundle as keyof typeof WRITE_ACTION_BUNDLES];
  return units?.includes(action as never) ?? false;
}

export function expandWriteActions(
  actions: readonly (WriteAction | "*")[],
): (WriteAction | "*")[] {
  if (actions.includes("*")) return ["*"];
  const expanded = new Set<WriteAction>();
  for (const action of actions) {
    if (action === "*") continue;
    expanded.add(action);
    if (!Object.hasOwn(WRITE_ACTION_BUNDLES, action)) continue;
    const units = WRITE_ACTION_BUNDLES[action as keyof typeof WRITE_ACTION_BUNDLES];
    for (const unit of units ?? []) expanded.add(unit);
  }
  return [...expanded];
}

/**
 * #3450: the write actions /api/rbac/me may list, from the list a /me path
 * resolved. Every entry is kept except a bundle name whose units are not all
 * listed beside it: the client expands a bundle name back into every unit
 * (expandWriteActions in useCanWriteAction), so a name listed over a revoked
 * unit shows that unit's control. The held units stay listed on their own.
 * It only removes entries, so it can never list an action the input lacked.
 * /me passes its final list through this once, so every path that adds to
 * that list (role rows, per-user overrides, any later source) is covered.
 */
export function listableWriteActions(
  actions: readonly (WriteAction | "*")[],
): (WriteAction | "*")[] {
  const listed = new Set<WriteAction | "*">(actions);
  return actions.filter((action) => {
    if (action === "*") return true;
    if (!Object.hasOwn(WRITE_ACTION_BUNDLES, action)) return true;
    const units: readonly WriteAction[] | undefined =
      WRITE_ACTION_BUNDLES[action as keyof typeof WRITE_ACTION_BUNDLES];
    return units === undefined || units.every((unit) => listed.has(unit));
  });
}

export function isWriteAllowed(action: WriteAction, access: RoleDataAccess): boolean {
  const actions = access.allowed_write_actions;
  if (!actions || actions.length === 0) return false;
  if (actions.includes("*") || actions.includes(action)) return true;
  return actions.some(
    (candidate): candidate is WriteAction =>
      candidate !== "*" && bundleIncludes(candidate, action),
  );
}

function writeOverrideMatches(key: string, action: WriteAction): boolean {
  if (!key.startsWith("write:")) return false;
  const candidate = key.slice("write:".length) as WriteAction;
  return candidate === action || bundleIncludes(candidate, action);
}

/**
 * Whether a per-user `write:` revoke covers this action (the action itself or
 * a bundle containing it). Exported so the portable-capability path (#3304)
 * honours the same revoke the role path does: revoke wins over every grant.
 */
export function isWriteRevokedByOverride(
  action: WriteAction,
  overrides: UserFeatureOverrides,
): boolean {
  return overrides.revokes.some((key) => writeOverrideMatches(key, action));
}

/**
 * #3304 panel: whether a per-user revoke takes this action away from a
 * PORTABLE holder. A unit is revoked by its own key or its bundle's; a bundle
 * name is revoked by its own key or by a revoke of ANY of its units, because
 * the client expands a bundle name back into every unit. One rule for the
 * route gate (hasWriteAction) and /api/rbac/me, so the two cannot disagree.
 */
export function isPortableWriteRevoked(
  action: WriteAction,
  overrides: UserFeatureOverrides,
): boolean {
  if (isWriteRevokedByOverride(action, overrides)) return true;
  const units = WRITE_ACTION_BUNDLES[action as keyof typeof WRITE_ACTION_BUNDLES];
  return units?.some((unit) => isWriteRevokedByOverride(unit, overrides)) ?? false;
}

/** Per-user grant/revoke resolution for write actions; revoke wins over every role. */
export function isWriteAllowedWithOverrides(
  action: WriteAction,
  access: RoleDataAccess,
  overrides: UserFeatureOverrides,
): boolean {
  if (isWriteRevokedByOverride(action, overrides)) {
    return false;
  }
  if (overrides.grants.some((key) => writeOverrideMatches(key, action))) {
    return true;
  }
  return isWriteAllowed(action, access);
}

/**
 * #4733: the write actions GET /api/rbac/me resolves for a user, before
 * listableWriteActions. The roles' union list expanded or, once any per-user
 * `write:` override exists, every action the override resolution admits;
 * then the actions the portable capabilities carry, less their revokes.
 * /me answers with it, and the #4586 dark sweep resolves each holder of a
 * role with it, so the write gate the sweep models reads the list the
 * client's useCanWriteAction reads.
 */
export function resolvedWriteActions(
  access: RoleDataAccess,
  overrides: UserFeatureOverrides,
  portableCapabilities: readonly PortableCapability[],
): (WriteAction | "*")[] {
  const hasWriteOverrides = [...overrides.grants, ...overrides.revokes].some((key) => key.startsWith("write:"));
  const actions: (WriteAction | "*")[] = hasWriteOverrides
    ? ALL_WRITE_ACTIONS.map((entry) => entry.name).filter((action) => isWriteAllowedWithOverrides(action, access, overrides))
    : expandWriteActions(access.allowed_write_actions ?? []);
  if (actions.includes("*")) return actions;
  const portable = portableCapabilities
    .flatMap((capability) => portableCapabilityWriteActions(capability))
    .filter((action) => !isPortableWriteRevoked(action, overrides));
  return [...new Set([...actions, ...portable])];
}

/**
 * #423: the page list GET /api/rbac/me hands the client before its RDTI
 * steps. With no override it is the roles' union as is; with any, every
 * catalog page the override resolution admits, because the client's prefix
 * match cannot express "wildcard minus one page". #4733: the dark sweep
 * resolves each holder's pages with it.
 */
export function pagesWithOverrides(allowedPages: string[], overrides: UserFeatureOverrides): string[] {
  if (overrides.grants.length === 0 && overrides.revokes.length === 0) return allowedPages;
  return ALL_PAGE_PATHS.filter((page) => isPathAllowedWithOverrides(page, allowedPages, overrides));
}

/**
 * The page list GET /api/rbac/me hands the client: pagesWithOverrides, then
 * /rdti for a holder of the portable RDTI entry (#3304, rdti.log from a role
 * row or the bundle:rdti_broad grant), then off again on a per-user revoke of
 * the page, its API or the bundle, which wins over the entry and the role
 * list alike (the middleware refuses /api/rdti on the same revoke, so the
 * page would only load to fail). #4733 round 3: /me and the dark sweep both
 * call it, so a holder's pages are the client's.
 */
export function meAllowedPages(rolePages: string[], overrides: UserFeatureOverrides, rdtiEntry: boolean): string[] {
  let pages = pagesWithOverrides(rolePages, overrides);
  if (rdtiEntry) pages = unionGrantLists([pages, ["/rdti"]]);
  if (
    overrides.revokes.includes("bundle:rdti_broad") ||
    isPathRevokedByOverride("/rdti", overrides) ||
    isPathRevokedByOverride("/api/rdti", overrides)
  ) {
    pages = pages.filter((page) => page !== "/rdti");
  }
  return pages;
}

/**
 * #446: a role with no AI tools (and no "*" wildcard) is "AI-off" — the Data
 * Analyst isn't available to it (e.g. qc_dashboards, viewer). The chat entry
 * points reject these rather than serving a tool-less agent: saves tokens and
 * enforces the role's documented intent ("You do not have access to the AI
 * Data Analyst"). Fail-closed: an unresolved role falls back to viewer ([]) → off.
 */
export function isAiOffRole(access: RoleDataAccess): boolean {
  return !access.allowed_tools.includes("*") && access.allowed_tools.length === 0;
}

/**
 * Check if a query_ostendo resource is allowed for the given access config.
 * Deny by default: returns true only when `allowed_ostendo_resources` is a
 * defined array that explicitly names the resource.
 *   - `allowed_ostendo_resources` undefined (no slice configured) denies
 *     every resource.
 *   - `allowed_ostendo_resources` as `[]` (empty array) also denies every
 *     resource, useful for staging removal of Ostendo access without
 *     yanking the binary tool gate.
 *   - Otherwise, only resources named in the array are allowed.
 *
 * GH #2850 / #2892: the composite resources approved_stock and company_stock
 * are ABSENT from the resource catalog (OSTENDO_RESOURCE_CATALOG), and the
 * slice validator (src/server/routes/rbac.ts) refuses those two names in
 * `allowed_ostendo_resources`, so this function can never return true for
 * them via a slice — they carry their own routing in that sense. But it is
 * NOT true that they skip this function: query-ostendo-handlers.ts calls it
 * for every resource, composites included, before routing. What that means
 * per mode:
 *   - RBAC v2 not authoritative, role UNSLICED (`allowed_ostendo_resources`
 *     undefined): this function is never consulted for the composite (the
 *     caller's `sliceDenied` check short-circuits on `!== undefined`), so
 *     the composite falls open — catalogue routing only.
 *   - RBAC v2 not authoritative, role SLICED: this function returns false
 *     for the composite unconditionally (it can never be in the slice), so
 *     a sliced role is always denied the composite pre-v2.
 *   - RBAC v2 authoritative: this function always returns false for the
 *     composite (same reason), so the slice path always denies it; the
 *     capability path (authorizeProjectedOstendo, consulted only when this
 *     function denies) is what can still allow it, via a manifest capability
 *     whose dependencies name `<db>:approved_stock:read:projected` or
 *     `<db>:company_stock:read:projected` (inventory.approved_stock /
 *     inventory.company_stock, #2892). The other escape consulted on a
 *     denial, the exact generic grant (#2963), cannot open a composite:
 *     authorizeGenericOstendo requires the tuple in the manifest's
 *     generic_resources, and the composites are absent there too.
 */
export function isOstendoResourceAllowed(resource: string, access: RoleDataAccess): boolean {
  if (access.allowed_ostendo_resources === undefined) return false;
  return access.allowed_ostendo_resources.includes(resource);
}

/**
 * #2877: the one decision both consumers of an Ostendo resource grant must
 * agree on, query-ostendo-handlers.ts (which runs the queries) and
 * prompt-suggestions.ts (which decides whether to resurface a past prompt
 * that used one). Before this existed, prompt-suggestions called
 * `isOstendoResourceAllowed` directly and query-ostendo-handlers implemented
 * its own pre-v2 unsliced-allow exception inline, so an unsliced role (every
 * live prod role today) got query results but no suggestions for the same
 * resource.
 *
 * `v2Authoritative` is `capabilityGrantsExist(d1)`, whether THIS realm's
 * RBAC v2 capability manifest is live. Until it is, an undefined slice is
 * treated as allow (main's documented unsliced semantics, see
 * `allowed_ostendo_resources` above); once it is, this defers entirely to
 * `isOstendoResourceAllowed`, matching its deny-on-undefined contract. That
 * function's own behaviour and tests (#2850) are unchanged by this helper:
 * it is consulted, never reimplemented.
 *
 * `crosIntent` (#2891) folds in the #2173 CROS purchasing id-gate: an
 * unsliced role reaching a CROS_PURCHASING_RESOURCES resource with CROS
 * intent (the call named `query_ostendo_cros` or passed `db: "CROS"`) is
 * denied unless one of its role ids is in CROS_PURCHASING_UNSLICED_ROLE_IDS,
 * whatever v2Authoritative is. Checked FIRST, ahead of both branches below,
 * because it is the one deny that never gets the generic or projected grant
 * escapes a v2-authoritative denial can otherwise use (see the reason string:
 * callers skip both lookups when it contains "#2173"). Defaults to
 * `false` so every pre-#2891 call site (there are none left in this repo,
 * but a future one) keeps the old sliced/unsliced-only behaviour.
 * query-ostendo-handlers.ts (runs the query) and prompt-suggestions.ts
 * (decides whether to resurface a past prompt) both pass their call's real
 * crosIntent now, so this rule cannot drift between them again, same as the
 * unsliced-allow exception above.
 */
export function ostendoResourceVerdict(
  access: RoleDataAccess | undefined,
  resource: string,
  v2Authoritative: boolean,
  crosIntent = false,
): { allow: boolean; reason: string } {
  if (
    access &&
    access.allowed_ostendo_resources === undefined &&
    crosIntent &&
    (CROS_PURCHASING_RESOURCES as readonly string[]).includes(resource) &&
    !(access.role_ids ?? []).some((id) => (CROS_PURCHASING_UNSLICED_ROLE_IDS as readonly string[]).includes(id))
  ) {
    return { allow: false, reason: "unsliced-deny (#2173 CROS purchasing id-gate)" };
  }
  if (v2Authoritative) {
    // No access at all is a deny, never a crash: isOstendoResourceAllowed
    // reads `access.allowed_ostendo_resources` and throws on undefined
    // `access` itself (distinct from an undefined SLICE on a real access
    // object, which it handles fine). An authz verdict must never throw.
    if (!access) return { allow: false, reason: "no-access-deny (RBAC v2 manifest authoritative)" };
    const allow = isOstendoResourceAllowed(resource, access);
    if (allow) return { allow: true, reason: "sliced-allow" };
    return {
      allow: false,
      reason: access.allowed_ostendo_resources === undefined
        ? "unsliced-deny (RBAC v2 manifest authoritative)"
        : "sliced-deny",
    };
  }
  if (access?.allowed_ostendo_resources === undefined) {
    return { allow: true, reason: "unsliced-allow (pre-v2 legacy default)" };
  }
  const allow = isOstendoResourceAllowed(resource, access);
  return { allow, reason: allow ? "sliced-allow" : "sliced-deny" };
}

// Keywords that END a FROM region at paren depth 0 (the relation list is over).
// NOTE: JOIN / ON / INNER / LEFT etc. are deliberately NOT here — they live
// INSIDE the FROM region and introduce more relations, so the region must span
// them. (A comma relation can legally follow an ON predicate: `FROM a JOIN b ON
// a.id=b.id, c` — c is a FROM-level relation.)
const FROM_REGION_END = new Set([
  "where", "group", "order", "having", "limit", "offset", "union", "except",
  "intersect", "window", "fetch", "for", "returning", "into", "set",
]);
// Tokens that can appear in relation position but are NOT table names.
const NON_TABLE_RELATION = new Set(["select", "lateral", "unnest", "values", "where"]);

/** Split a clause on top-level commas (commas not inside parentheses). */
function splitTopLevelCommas(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let last = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === "(") depth++;
    else if (c === ")") depth = Math.max(0, depth - 1);
    else if (c === "," && depth === 0) {
      out.push(s.slice(last, i));
      last = i + 1;
    }
  }
  out.push(s.slice(last));
  return out;
}

/**
 * Resolve the table name from a single relation token like `schema.tbl alias`,
 * `"tbl"`, or `tbl AS t`. Returns null for a parenthesised relation (subquery /
 * VALUES / set-returning function) — any tables INSIDE such a relation are
 * picked up independently by the FROM/JOIN keyword scan over the full query.
 *
 * Schema handling (#1260): a `public.` qualifier is stripped (every Django
 * table lives there, and prefix grants like `qa_` must keep matching), but any
 * OTHER schema stays attached — reducing `information_schema.columns` to bare
 * `columns` made isTableNameAllowed's introspection exemption miss it, false-
 * denying the analyst's mandatory schema-discovery flow for scoped roles.
 * Keeping an unknown schema attached is the fail-closed direction: a name like
 * `sales.orders` now has to match a grant as written, not via its bare tail.
 */
function relationTableName(rel: string): string | null {
  const t = rel.trim();
  if (!t || t.startsWith("(")) return null;
  const firstToken = t.split(/[\s(]/)[0] ?? "";
  const parts = firstToken.replace(/"/g, "").split(".");
  if (parts.some((p) => !/^\w+$/.test(p))) return null;
  const bare = parts[parts.length - 1]?.toLowerCase() ?? "";
  if (parts.length < 2) return bare || null;
  const schema = parts[parts.length - 2].toLowerCase();
  return schema === "public" ? bare : `${schema}.${bare}`;
}

/**
 * Slice the FROM region: from `start` to the next top-level (paren-depth-0)
 * FROM_REGION_END keyword, `;`, or the close of an enclosing paren. JOIN/ON
 * stay inside the region. The boundary word is matched as a FULL identifier so
 * `inner_t` is not mistaken for the `INNER` keyword.
 */
function sliceFromRegion(query: string, start: number): string {
  let depth = 0;
  let i = start;
  for (; i < query.length; i++) {
    const c = query[i];
    if (c === "(") { depth++; continue; }
    if (c === ")") { if (depth === 0) break; depth--; continue; }
    if (c === ";") break;
    if (
      depth === 0 &&
      /[A-Za-z_]/.test(c) &&
      !/[A-Za-z0-9_]/.test(query[i - 1] ?? " ")
    ) {
      const word = (query.slice(i).match(/^[A-Za-z_][A-Za-z0-9_]*/)?.[0] ?? "").toLowerCase();
      if (FROM_REGION_END.has(word)) break;
    }
  }
  return query.slice(start, i);
}

/** Add a comma-segment's relations to the set: leading relation + each JOIN's. */
function collectRelations(segment: string, out: Set<string>): void {
  const lead = relationTableName(segment);
  if (lead && !NON_TABLE_RELATION.has(lead)) out.add(lead);
  const joinRe = /\bJOIN\s+/gi;
  while (joinRe.exec(segment) !== null) {
    const name = relationTableName(segment.slice(joinRe.lastIndex));
    if (name && !NON_TABLE_RELATION.has(name)) out.add(name);
  }
}

/**
 * Blank out string literals and comments so structural scans can't be spoofed
 * by SQL text INSIDE data (e.g. a WHERE literal containing "WITH x AS (").
 * Replacement preserves length-ish structure but removes all content. Used for
 * CTE-name discovery only — the table scan keeps reading the raw query, where
 * literal blindness errs toward OVER-collection (safe direction).
 */
function stripLiteralsAndComments(query: string): string {
  return query
    .replace(/'(?:[^']|'')*'/g, "''") // single-quoted strings incl. '' escapes
    .replace(/--[^\n\r]*/g, "") // #5035: \n or \r ends a -- comment, as in PostgreSQL
    .replace(/\/\*[\s\S]*?\*\//g, "");
}

/**
 * Names declared as CTEs (`WITH [RECURSIVE] name [(cols)] AS (…), name2 AS (…)`)
 * are candidate relation aliases. This text scan does NOT resolve CTE scope:
 * a qualified name, a sibling declared later, or a nested WITH can each hide a
 * real table behind a CTE name (#3053). It is no longer part of any access
 * decision: run_sql is gated by the scope-aware parse in server/lib/sql-gate.ts.
 *
 * Why this exists (2026-07-29 conversation-log audit): the run_sql tool
 * description tells the analyst to "use CTEs for complex analysis", and the
 * gate then denied the CTE names as unknown tables — the single largest
 * error class in the July logs (names like prev_product, lot_summary,
 * changeovers) was users with legitimate access being false-denied.
 */
export function extractCteNames(query: string): Set<string> {
  const src = stripLiteralsAndComments(query);
  const out = new Set<string>();
  const withRe = /\bWITH\s+(?:RECURSIVE\s+)?/gi;
  let wm: RegExpExecArray | null;
  while ((wm = withRe.exec(src)) !== null) {
    let i = wm.index + wm[0].length;
    // Read a `name [(cols)] AS [NOT] [MATERIALIZED] ( …balanced… )` chain,
    // comma-separated. Anything that doesn't match the shape ends the chain —
    // a non-CTE WITH (e.g. inside a word boundary miss) simply contributes
    // nothing rather than guessing.
    for (;;) {
      const m = src
        .slice(i)
        .match(/^\s*([A-Za-z_][\w$]*)\s*(?:\([^()]*\)\s*)?AS\s+(?:NOT\s+)?(?:MATERIALIZED\s+)?\(/i);
      if (!m) break;
      out.add(m[1].toLowerCase());
      // Walk to the CTE body's matching close paren.
      let depth = 1;
      let j = i + m[0].length;
      while (j < src.length && depth > 0) {
        if (src[j] === "(") depth++;
        else if (src[j] === ")") depth--;
        j++;
      }
      const comma = src.slice(j).match(/^\s*,/);
      if (!comma) break;
      i = j + comma[0].length;
    }
  }
  return out;
}

/**
 * Extract table names referenced in a SQL query (best-effort text scan).
 *
 * #3053/#3234/#3241: this is no longer the gate. server/lib/sql-gate.ts parses
 * the query and resolves every relation. The scan stays for its tests and as
 * the rebuilt old gate in tests/run-sql-relation-gate.test.ts's negative
 * control; do not use it for access decisions.
 *
 * Catches `FROM t`, `JOIN t`, tables inside subqueries (the FROM/JOIN keyword
 * scan runs over the whole query, so nested clauses are visited), AND
 * comma-separated relations: `FROM a, b, c`.
 *
 * #495/#5 fix: the previous regex captured only the FIRST relation after each
 * FROM/JOIN, so `FROM a, b` yielded just `[a]`. A role whose
 * allowed_table_prefixes excluded `crosbe_` (rd, qa_tech) could therefore read
 * CRM/Sales tables by comma-joining — `SELECT * FROM qa_production_x,
 * crosbe_customers` — because checkTableAccess never saw `crosbe_customers`.
 * We now split each FROM/JOIN clause on top-level commas so every relation is
 * checked. Unknown / unparseable names fall through to the prefix check, which
 * denies them (fail-closed).
 */
export function extractTableNames(query: string): string[] {
  const tables = new Set<string>();
  // Every FROM (including those inside subqueries — the scan ignores depth, so
  // nested FROMs are visited and their regions captured independently). For each
  // FROM region, split on top-level commas; each segment contributes its leading
  // relation plus any JOIN-introduced relations.
  const fromRe = /\bFROM\b/gi;
  while (fromRe.exec(query) !== null) {
    const region = sliceFromRegion(query, fromRe.lastIndex);
    for (const seg of splitTopLevelCommas(region)) collectRelations(seg, tables);
  }
  // Declared CTE names are aliases, not tables — see extractCteNames.
  const ctes = extractCteNames(query);
  return [...tables].filter((t) => !ctes.has(t.toLowerCase()));
}

/**
 * #3241 / #3234: tables no role may read through the analyst, whatever its
 * `allowed_table_prefixes` say (a `"*"` grant included). Django's auth and
 * session tables hold password hashes and live crosbe-app session keys; the
 * shared `cai` connection can SELECT them (#864), so this list is the only
 * thing between a run_sql role and them.
 *
 * The named tables are pinned against DATABASE_SCHEMA.md by
 * tests/run-sql-relation-gate.test.ts (every entry must be a real table, and
 * every real `auth_` / `django_` table must be covered), so the list cannot
 * drift from the database silently. The prefixes cover tables added later.
 */
export const SQL_DENIED_TABLES: readonly string[] = [
  "auth_group",
  "auth_group_permissions",
  "auth_permission",
  "auth_user",
  "auth_user_groups",
  "auth_user_user_permissions",
  "django_admin_log",
  "django_content_type",
  "django_migrations",
  "django_session",
];

/** Table-name families no role may read: Django auth/session and the pg_ system views. */
export const SQL_DENIED_TABLE_PREFIXES: readonly string[] = ["auth_", "django_", "pg_"];

/** Schemas no role may name. Anything other than `public` is refused as well. */
export const SQL_DENIED_SCHEMAS: readonly string[] = ["information_schema", "pg_catalog", "pg_toast"];

/**
 * #3400: a catalogue identifier, a denied schema or any pg_ name. The run_sql
 * gate refuses these for every role (sql-gate.ts), and the #3400 existence
 * check arms on the same predicate, so the two cannot disagree on what a
 * catalogue lookup is.
 */
export function isSqlCatalogueIdentifier(id: string): boolean {
  const lower = id.toLowerCase();
  return SQL_DENIED_SCHEMAS.includes(lower) || lower.startsWith("pg_");
}

/**
 * True when no role may read this relation. `name` is lower-case, either bare
 * (`auth_user`) or schema-qualified (`information_schema.columns`); any schema
 * other than `public` is denied.
 */
export function isSqlRelationDenied(name: string): boolean {
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot !== -1) {
    const schema = lower.slice(0, dot);
    if (schema !== "public") return true;
    return isSqlRelationDenied(lower.slice(dot + 1));
  }
  if (SQL_DENIED_SCHEMAS.includes(lower)) return true;
  if (SQL_DENIED_TABLES.includes(lower)) return true;
  return SQL_DENIED_TABLE_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

/**
 * The per-table team-boundary primitive: is one table name reachable under a
 * role's `allowed_table_prefixes`? The absolute deny list above wins over
 * everything; then `"*"` allows all; otherwise the name must start with a
 * granted prefix. This is the single point of truth for "which team's tables
 * can this role touch", used by the AI SQL gate (server/lib/sql-gate.ts), the
 * analyst's list_tables / get_table_schema tools and the Data Explorer table
 * browser (#830), so a role browses exactly the table families its role
 * grants, never another team's (a QC grant never surfaces NATA Lab customer
 * PII, R&D IP, etc.).
 *
 * #3234: system catalogues (pg_*, information_schema) are no longer exempt.
 * pg_stats.most_common_vals returns real column values from any table and
 * pg_stat_activity shows other users' SQL; the analyst discovers the schema
 * through list_tables and get_table_schema instead.
 */
export function isTableNameAllowed(table: string, allowedPrefixes: readonly string[]): boolean {
  if (isSqlRelationDenied(table)) return false;
  const lower = table.toLowerCase();
  const bare = lower.startsWith("public.") ? lower.slice("public.".length) : lower;
  if (allowedPrefixes.includes("*")) return true;
  // #1835: "=" exact grants count only in a code-defined list (shared/table-grants.ts);
  // in a stored role a "=" entry is a prefix no table name starts with.
  const exactOk = honoursExactGrants(allowedPrefixes);
  return allowedPrefixes.some((grant) =>
    grant.startsWith(EXACT_TABLE_MARK)
      ? exactOk && grant.length > EXACT_TABLE_MARK.length && bare === grant.slice(EXACT_TABLE_MARK.length)
      : bare.startsWith(grant),
  );
}

/**
 * Universal authenticated pages — reachable by ANY signed-in user regardless of role,
 * because they're personal/utility chrome (own account config, the changelog, the help
 * guide), not role-specific business data. They must NOT be subject to allowed_pages
 * gating: a QC-Dashboards (or any non-admin) user still needs their own Settings.
 *
 * Used by the AppShell route-guard (#446) and the sidebar filter so these pages are both
 * reachable and visible for every role. (#374 follow-up: qc_dashboards was bounced off
 * /settings — the UserMenu's Settings link silently redirected to the role's first page.)
 *
 * #3502: "/shared" is the Data Analyst share link (/shared/:token). It is not a catalog
 * page, so no nav item or role checkbox exists for it; it is here only so the AppShell
 * guard stops bouncing every recipient without the page wildcard. The token and
 * GET /api/sharing/:token (session plus the /api/sharing API grant) still decide what
 * a recipient can read. The sidebar, the Roles screen and the reachability checks walk
 * catalog pages only, so this entry changes nothing they show.
 *
 * #593 TDSVIEW: "/data-sheets" is the issued TDS for every signed-in role (Jez
 * and Akira, 14 Oct: "anyone would be allowed to get the TDS to view and
 * download"). Its API, /api/tds-sheets, is admitted the same way server-side
 * (rbacMiddleware's SIGNED_IN_ROLE_READ_PATTERNS). PENDING is refused that API
 * at the gate whatever its role row holds, so a pending user who opens the
 * page sees the refusal and no sheet.
 */
export const UNIVERSAL_PAGES = ["/settings", "/updates", "/guide", "/shared", "/data-sheets"];

/**
 * #3338: the slug that marks the admin role. The server refuses edits to a
 * role holding it (routes/rbac.ts, via role_slugs); the Roles screen renders
 * that role read-only by the same test on the role_slug GET /api/rbac/roles
 * returns (joined from the same table), so the two cannot disagree about
 * which role is admin and the screen never offers a form the API always 400s.
 */
export const ADMIN_ROLE_SLUG = "admin";

export function isAdminRoleRow(role: { role_slug?: string | null }): boolean {
  return role.role_slug === ADMIN_ROLE_SLUG;
}

/**
 * #593 security review: a universal page an admin can still take away from one
 * user. Each maps to the API prefix it reads, so ONE per-user revoke, set on the
 * page in the admin override UI (`/data-sheets`, a catalog path and so a valid
 * feature key), closes both: rbacMiddleware refuses the API, /api/rbac/me lists
 * the page in `revoked_pages`, and isUniversalPage stops exempting it.
 */
export const REVOCABLE_UNIVERSAL_PAGES: Readonly<Record<string, string>> = {
  "/data-sheets": "/api/tds-sheets",
};

/** The revocable universal pages a user's revoke list takes away. */
export function revokedUniversalPages(revokes: readonly string[]): string[] {
  return Object.keys(REVOCABLE_UNIVERSAL_PAGES).filter(
    (page) => !isPathAllowedWithOverrides(page, ["*"], { grants: [], revokes: [...revokes] }),
  );
}

/**
 * The revocable universal page whose API this path is under, when that page is
 * revoked for the user; null otherwise. rbacMiddleware refuses such a path.
 */
export function revokedUniversalPageForApi(path: string, revokes: readonly string[]): string | null {
  for (const [page, api] of Object.entries(REVOCABLE_UNIVERSAL_PAGES)) {
    if (path === api || path.startsWith(api + "/")) {
      return revokedUniversalPages(revokes).includes(page) ? page : null;
    }
  }
  return null;
}

/**
 * True if the path is a universal authenticated page (exempt from allowed_pages
 * gating). `revoked` is /api/rbac/me's `revoked_pages`: a revocable universal
 * page listed there is not universal for this user (#593).
 */
export function isUniversalPage(path: string, revoked: readonly string[] = []): boolean {
  if (revoked.some((p) => path === p || path.startsWith(p + "/"))) return false;
  return UNIVERSAL_PAGES.some((p) => path === p || path.startsWith(p + "/"));
}

/**
 * Resolve a route to its canonical catalog Page through PATH_ALIASES. Most
 * entries are retired-path merges; a multi-route Page such as BOM Suite also
 * uses the map to make its live tab routes one RBAC unit. Sub-routes carry
 * across: "/old/x" -> "/new/x". The injectable map keeps the pure matching
 * behaviour easy to exercise in tests.
 */
export function canonicalPath(
  path: string,
  aliases: Record<string, string> = PATH_ALIASES
): string {
  if (Object.hasOwn(aliases, path) && aliases[path]) return aliases[path];
  for (const [oldPath, newPath] of Object.entries(aliases)) {
    if (path.startsWith(oldPath + "/")) return newPath + path.slice(oldPath.length);
  }
  return path;
}

/**
 * Check if a path is allowed by a list of path prefixes.
 * Supports "*" wildcard for full access.
 */
export function isPathAllowed(
  path: string,
  allowedPrefixes: string[],
  aliases: Record<string, string> = PATH_ALIASES
): boolean {
  if (allowedPrefixes.includes("*")) return true;
  // Canonicalise BOTH sides so RBAC follows both retired merges and live
  // multi-route Pages. Historical grants keep working, and any equivalent route
  // grant resolves to the Page represented by its single catalog checkbox.
  const p = canonicalPath(path, aliases);
  return allowedPrefixes.some((prefix) => {
    const cp = canonicalPath(prefix, aliases);
    // #827: a blank/malformed prefix must grant nothing. "" used to degenerate to
    // p.startsWith("/") — true for every page — turning one leaked sentinel row
    // (the sidebar divider entries carry path: "") into an allow-everything grant.
    if (!cp.startsWith("/")) return false;
    return p === cp || p.startsWith(cp + "/");
  });
}

/**
 * Filter command-palette / nav-hint items down to those the role can actually
 * reach (#599 cluster — the palette previously advertised every page to every
 * role). Fail-closed: while the role is unknown (pages === null) we show
 * nothing, matching useCanAccess's "hide until role is known". Query strings on
 * item paths (e.g. "/quality?product=x") are stripped before the prefix check.
 * The route guard + server RBAC remain the real enforcement; this only stops the
 * palette listing pages a role can't open (and the confusing bounce that follows).
 */
export function filterAllowedNavItems<T extends { path: string }>(
  items: T[],
  pages: string[] | null
): T[] {
  if (pages === null) return [];
  return items.filter((i) => isPathAllowed(i.path.split("?")[0], pages));
}
